import type { CrmConnections } from '../form-spam-data'
import { createCrmApi } from 'services/crm/api'
import { formatRelativeShort } from 'utils/time'
import { ChartPanelStatus } from 'components/charts/chart-panel-status'
import { errorMessage } from '../../lead-forms/api'
import { DotMatrix } from '@clickguard/design-system'
import { chartData } from '../../lead-forms/reporting-adapter'
import { useLeadFormStats } from '../../lead-forms/use-lead-form-stats'
import React, {
	createContext,
	createElement,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import { createPortal } from 'react-dom'
import { ResponsiveBar } from '@nivo/bar'
import type { BarItemProps } from '@nivo/bar'
// R87: both packages ship INSIDE `@nivo/bar` and are hoisted to the root of
// node_modules. A custom `barComponent` cannot be written without them: the
// tooltip hook is how a bar raises the hover card, and the spring values that
// nivo hands the component are only readable through `to()`.
import { useTooltip } from '@nivo/tooltip'
import { animated, to } from '@react-spring/web'
import { ResponsiveLine } from '@nivo/line'
import {
	AppButton,
	ChartTooltip,
	ChartTooltipList,
	ChartTooltipRow,
	ChartTooltipSwatch,
	ChartTooltipTitle,
	ChartLegend,
	ChartLegendRow,
	Checkbox,
	Divider,
	IconButton,
	ListItem,
	MenuList,
	MenuListDivider,
	MetricTile,
	Modal,
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs,
	Sheet,
	StatCard,
	Tab,
	TabList,
	Tabs,
	Tooltip,
	useAnchoredPlacement,
	anchoredRight,
	anchoredTop,
	useEnterExit,
	useInert,
	usePopoverMotion,
	zLayers
} from '@clickguard/design-system'
import type {
	AnchorStrategy,
	ChartLegendColumn
} from '@clickguard/design-system'
import BarChart01 from '@untitled-ui/icons-react/build/esm/BarChart01'
import FilterLines from '@untitled-ui/icons-react/build/esm/FilterLines'
import Flag01 from '@untitled-ui/icons-react/build/esm/Flag01'
import InfoCircle from '@untitled-ui/icons-react/build/esm/InfoCircle'
import LineChartUp01 from '@untitled-ui/icons-react/build/esm/LineChartUp01'
import LinkExternal01 from '@untitled-ui/icons-react/build/esm/LinkExternal01'
import Minus from '@untitled-ui/icons-react/build/esm/Minus'
import PieChart01 from '@untitled-ui/icons-react/build/esm/PieChart01'
import Send01 from '@untitled-ui/icons-react/build/esm/Send01'
import ShieldOff from '@untitled-ui/icons-react/build/esm/ShieldOff'
// R108: the SAME ramp arrow the metric cards use (`percentage-new-badge.tsx`).
// The DS ships no trend primitive, and this pack is already a platform
// dependency, so the legend delta and the metric tiles read as one language.
import { FaArrowTrendDown, FaArrowTrendUp } from 'react-icons/fa6'
import {
	BOT_OUTCOMES,
	BotLevel,
	CaptchaTrigger,
	CrmAccount,
	EMAIL_OUTCOMES,
	FormRecord,
	OutcomeDef,
	OutcomePoint,
	SETUP_WARN,
	VerdictPoint,
	VerdictTotals,
	botLevelOf,
	botLevelPatch,
	formatCount,
	formatShare,
	getBotOutcomeSeries,
	getEmailOutcomeSeries,
	getLinkedLabel,
	getVerdictSeries,
	isWorseWhenUp,
	needsSetup,
	outcomeTotals,
	previousOutcomeTotals,
	useFormSpam,
	useSwapKey,
	verdictTotals
} from '../form-spam-data'
import {
	AuthorizeDialog,
	CONFIRM_MODAL_WIDTH,
	CloseButton,
	DialogHeader,
	RequestSourceDialog,
	RequestState,
	useAuthorization,
	useCrmConnect,
	useCrmField,
	useWidgetCheck
} from './form-settings-shared'
import {
	BotPane,
	CrmPane,
	DetailsPane,
	EmailPane,
	useUrlVerify,
	widgetAction
} from './form-setup-panes'
import { FORM_SPAM_KB_URL } from './form-spam-refunds-agent-tokens'
import { useIsDarkTheme } from './use-form-spam-theme'

interface FormDetailDrawerProps {
	/** Which form to show. `null` closes the drawer. */
	formId: string | null
	onClose: () => void
	/**
	 * R298 (Gustavo, 11 Sep, pin #100): the submissions feed lives on the
	 * page's Submissions tab now, not in this drawer. The header's View
	 * submissions action hands the form id here; the page closes the drawer
	 * and opens `?tab=submissions&form=<id>` with the Form filter set.
	 */
	onViewSubmissions?: (formId: string) => void
}

/** Figma heatmap ramp (node 45:35708). One color per verdict, everywhere. */
const VERDICT_COLORS = {
	allow: '#b7b2ff',
	block: '#cecbff',
	challenge: '#e1dfff'
}

type Range = '7d' | '30d'

/* ------------------------------ chart card ------------------------------ */

/* R50: nivo paints its tooltip inside the chart's own container, so the card
   wrapper's `overflow-hidden` and the scrolling drawer body both cut the card
   off at the panel edge. `ChartTip` moves the card to `document.body` through
   a portal and pins it to the pointer in viewport coordinates, so no ancestor
   can clip it. Nothing in the DS fits: `useAnchoredPlacement` hangs a panel
   off an element, and a pointer is not an element. */
const TIP_GAP = 14
const TIP_EDGE = 8

/**
 * The last pointer position, in viewport coordinates. nivo hands the tooltip
 * the datum and not the event, so the card reads the pointer from here. The
 * listener sits at module level because the card must know the pointer on its
 * very first frame, before it has ever seen a move of its own.
 */
const pointer = { x: 0, y: 0 }

if (typeof document !== 'undefined')
	document.addEventListener(
		'mousemove',
		(event) => {
			pointer.x = event.clientX
			pointer.y = event.clientY
		},
		{ passive: true }
	)

const ChartTip = ({ children }: { children: React.ReactNode }) => {
	const ref = useRef<HTMLDivElement | null>(null)

	// A direct style write, not state: the card follows every move, and a
	// re-render per move would churn the whole chart's tooltip tree.
	const place = useCallback(() => {
		const node = ref.current
		if (!node) return
		const { width, height } = node.getBoundingClientRect()
		const right = window.innerWidth - TIP_EDGE
		const bottom = window.innerHeight - TIP_EDGE
		// Flip to the other side of the pointer when the near side does not
		// fit, then clamp, so all four edges stay inside the window.
		const left =
			pointer.x + TIP_GAP + width > right
				? pointer.x - TIP_GAP - width
				: pointer.x + TIP_GAP
		const top =
			pointer.y + TIP_GAP + height > bottom
				? pointer.y - TIP_GAP - height
				: pointer.y + TIP_GAP
		node.style.left = `${Math.max(TIP_EDGE, Math.min(left, right - width))}px`
		node.style.top = `${Math.max(TIP_EDGE, Math.min(top, bottom - height))}px`
	}, [])

	// Every render, before paint. The card is in the DOM by now, so its size is
	// real and the first painted frame already sits at the clamped spot: the
	// card never flashes at the top-left corner.
	useLayoutEffect(place)

	useEffect(() => {
		document.addEventListener('mousemove', place, { passive: true })
		return () => document.removeEventListener('mousemove', place)
	}, [place])

	return createPortal(
		<div ref={ref} className="pointer-events-none fixed left-0 top-0 z-[2000]">
			{children}
		</div>,
		document.body
	)
}

const GRID_ROWS = ['100%', '80%', '60%', '40%', '20%', '0%']

/**
 * The Figma chart bed: six labelled hairlines, 100% down to 0%. The plot
 * layer is absolute and spans from the center of the top line to the center
 * of the bottom line (the label column is 26px + an 8px gap = 34px).
 * `rows` swaps the percentage labels for counts (the Round 30 bar charts).
 *
 * R303 (Esteban, 11 Sep): "make the charts fit the container ... by making the
 * bars taller (graph part) not the other stuff". The bed is a column that
 * GROWS: `justify-between` spreads the six rows over any extra height its
 * parent grants, so the hairlines stay evenly spaced and the plot layer still
 * spans the top line's centre to the bottom line's centre. With no extra
 * height the rows sit at their own 208px rhythm, the same as before.
 */
const ChartBed = ({
	children,
	rows = GRID_ROWS
}: {
	children: React.ReactNode
	rows?: string[]
}) => (
	<div className="relative flex w-full grow flex-col justify-between">
		{rows.map((row, index) => (
			<div
				key={row}
				className={`flex items-center gap-2 ${
					index === 0
						? 'pb-2.5'
						: index === rows.length - 1
						? 'pt-2.5'
						: 'py-2.5'
				}`}
			>
				<span className="w-[26px] shrink-0 text-right text-[10px] leading-[18px] cg-text-text-secondary">
					{row}
				</span>
				<span className="h-px flex-1 cg-bg-subtle" />
			</div>
		))}
		<div className="absolute bottom-[9px] left-[34px] right-0 top-[9px]">
			{children}
		</div>
	</div>
)

/**
 * Bed labels for a count axis: a "nice" step (1, 2 or 5 times a power of
 * ten) whose five multiples cover the peak day. `max` pins the bar scale to
 * the top hairline so the bars and the labels agree.
 */
export const countRows = (peak: number) => {
	const rawStep = Math.max(1, peak / 5)
	const power = Math.pow(10, Math.floor(Math.log10(rawStep)))
	// 2.5 joins the ladder once it multiplies to an integer: without it the
	// jump from 100 to 250 leaves a peak of ~102 in the bottom 40% of the bed.
	const factors = power >= 10 ? [1, 2, 2.5, 5, 10] : [1, 2, 5, 10]
	const step =
		factors.map((m) => m * power).find((s) => s >= rawStep) ?? rawStep
	return {
		max: step * 5,
		rows: [5, 4, 3, 2, 1, 0].map((i) => formatCount(step * i))
	}
}

/**
 * Header-tile label with its own info tooltip. MetricTile's `info` slot
 * hard-wires the DS Tooltip default (`position="top"`) and forwards no
 * position; the drawer header hugs the viewport top, so that bubble clips
 * off-screen. Same DS Tooltip, opened below instead. DS gap to report:
 * MetricTile should expose the info tooltip position.
 */
const TileLabel = ({ text, info }: { text: string; info: string }) => (
	<span className="flex items-center gap-1.5">
		{text}
		<Tooltip content={info} position="bottom" portal>
			<span className="flex cursor-default cg-text-text-tertiary">
				<InfoCircle className="h-3 w-3" />
			</span>
		</Tooltip>
	</span>
)

const ChartHead = ({
	icon,
	title,
	hint,
	action
}: {
	icon: React.ReactNode
	title: string
	hint: string
	/** R134: a control at the right edge of the head, the way a StatCard's `trailing` slot sits. */
	action?: React.ReactNode
}) => (
	<div className="flex items-center gap-2 p-3">
		<div className="flex items-center gap-1.5 cg-text-text-primary">
			<span className="[&>svg]:h-3 [&>svg]:w-3">{icon}</span>
			<span className="text-[14px] font-medium leading-[18px]">{title}</span>
		</div>
		{/* The DS Tooltip, not the native title attribute: the native strip
		   needs a ~1s hover and reads as "no tooltip". Default top position
		   is safe here; the chart heads sit well below the viewport top. */}
		<Tooltip content={hint} portal>
			<span className="flex cursor-default cg-text-text-tertiary">
				<InfoCircle className="h-3 w-3" />
			</span>
		</Tooltip>
		{action ? <div className="ml-auto flex items-center">{action}</div> : null}
	</div>
)

/**
 * R232 (Gustavo, 3 Sep, pin #64): "the tooltip could have just the explanation
 * from each label". One row per outcome: the legend's word, then the one-line
 * meaning from the data table (`OutcomeDef.desc`). The old paragraph about
 * the series and the delta is gone; the subtitle now says what the bars count.
 * The DS Tooltip sets its content slot 12px semibold, so the rows keep the
 * size, reset to the regular weight, and keep the bold for the label alone.
 */
const OutcomeGlossary = ({ defs }: { defs: OutcomeDef[] }) => (
	<span className="flex flex-col gap-1.5 font-normal">
		{defs
			.filter((def) => def.desc)
			.map((def) => (
				<span key={def.key}>
					<span className="font-semibold">{def.label}</span>{' '}
					<span className="cg-text-on-tooltip-secondary">{def.desc}</span>
				</span>
			))}
	</span>
)

// nivo runs its own spring clock and the stat tween below runs on rAF, so
// the Tailwind duration / motion-reduce classes cannot reach either; this
// config and the media-query hook stand in for them.
const CHART_MOTION = {
	duration: 200,
	easing: (t: number) => 1 - Math.pow(1 - t, 3)
}

const usePrefersReducedMotion = () =>
	useMemo(
		() => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
		[]
	)

/**
 * Rolls a stat toward its target over the standard 200ms ramp, so the 7/30
 * flip redraws digits instead of hard-swapping them. Reduced motion snaps.
 * The timeout is the hidden-tab failsafe: rAF stops there, the timer does not.
 */
const useTweenedNumber = (target: number) => {
	const reduceMotion = usePrefersReducedMotion()
	const [shown, setShown] = useState(target)
	const shownRef = useRef(target)
	useEffect(() => {
		if (shownRef.current === target) return
		if (reduceMotion) {
			shownRef.current = target
			setShown(target)
			return
		}
		const from = shownRef.current
		const start = performance.now()
		let raf = 0
		const tick = (now: number) => {
			const t = Math.min((now - start) / CHART_MOTION.duration, 1)
			const next =
				t === 1 ? target : from + (target - from) * CHART_MOTION.easing(t)
			shownRef.current = next
			setShown(next)
			if (t < 1) raf = window.requestAnimationFrame(tick)
		}
		raf = window.requestAnimationFrame(tick)
		const failSafe = window.setTimeout(() => {
			shownRef.current = target
			setShown(target)
		}, CHART_MOTION.duration + 100)
		return () => {
			window.cancelAnimationFrame(raf)
			window.clearTimeout(failSafe)
		}
	}, [target, reduceMotion])
	return shown
}

/**
 * R53: cross-highlight, copied from the Protection analytics report
 * (`charts-row.tsx`, the `HiProps` type and the `hi` state in `ChartBody`).
 *
 * `hi` names the category that holds the panel's highlight right now, and
 * `onHi` sets it. One chart and the legend under it share ONE `hi`, so the
 * pointer over a legend row steps the other bars, arcs and lines back, and the
 * pointer over a bar or an arc steps the other legend rows back.
 *
 * The token is the LABEL, not the key, because two of the three charts already
 * key on the label: the mix's segments carry the label and the trend's series
 * ids are the labels. Only the stacked bar keys on `def.key`, and it maps.
 *
 * The two dim numbers differ on purpose, and both come from that report: a
 * chart shape drops to 0.25, a legend row to 0.40 (the DS `dim` prop). A row
 * carries text, so it needs the higher floor to stay readable.
 */
type HiProps = { hi: string | null; onHi: (label: string | null) => void }

const useHighlight = (): HiProps => {
	const [hi, setHi] = useState<string | null>(null)
	return { hi, onHi: setHi }
}

/**
 * The highlighted BAR KEY, for `OutcomeBar` only.
 *
 * `barComponent` must keep one identity for the life of the chart (see the
 * `OutcomeBar` note below), so the bar cannot close over `hi`. A context reads
 * the value at render time and leaves the component identity alone.
 */
const HiKeyContext = createContext<string | null>(null)

/** How far a chart shape steps back when another category holds the highlight. */
const DIM_OPACITY = 0.25

/**
 * R281 (Esteban, 4 Sep): the change against the earlier period is the move of
 * the SHARE, in percentage points, not the growth of the count. A count that
 * grows with the whole period says nothing about the mix; a share that moves
 * says the traffic changed. An empty period has no shares, so `shareOf`
 * answers 0 and never divides by zero.
 */
const shareOf = (part: number, total: number) =>
	total === 0 ? 0 : part / total

/**
 * R281: this period's share minus the earlier one, in percentage points,
 * rounded to one decimal. `-0` folds to `0`, so a share that fell by less than
 * 0.05 points prints `0.0` and not `-0.0`.
 */
const sharePoints = (
	value: number,
	total: number,
	prev: number,
	prevTotal: number
) => {
	const points =
		Math.round((shareOf(value, total) - shareOf(prev, prevTotal)) * 1000) / 10
	return points === 0 ? 0 : points
}

/**
 * R225 (Gustavo, 2 Sep, pin ked64q): a sign before every change. ONE function,
 * because the row prints it and nothing else may format it. R281: the number
 * is percentage points now (`sharePoints`). R288 (Esteban, 10 Sep): it carries
 * the unit, percentage points, because a bare `+5.3` under a `%` column reads
 * as a percent change, and it is not one. The space is non-breaking, so the
 * unit never wraps away from its number. R305 (Esteban, 11 Sep): "the PP
 * here should be lowercase", so `p.p.`: `+1.4 p.p.`, `-0.6 p.p.`, `0.0 p.p.`.
 */
const deltaText = (points: number) =>
	`${points > 0 ? '+' : points < 0 ? '-' : ''}${Math.abs(points).toFixed(
		1
	)}\u00a0p.p.`

/**
 * R108. What this outcome did against the period before, as one short line.
 * R281: the line is percentage points of the share (`sharePoints`), not the
 * growth of the count.
 *
 * Purple is the DEFAULT and takes no side: most categories simply moved, and a
 * rise in Valid is not a win the same way a rise in Spam trap is a loss. An
 * outcome where more is worse takes a side, and only then. A rise there is the
 * danger token and a fall is the success token, which is his rule: less of
 * these is better, more of these is bad.
 *
 * `isWorseWhenUp` reads the outcome table in `form-spam-data`, so this colour
 * can never disagree with the verdict the submissions list prints.
 *
 * No earlier period at all (an earlier TOTAL of zero) prints nothing. A share
 * of nothing is not a share, and a made up one would be a lie. An earlier
 * count of zero for ONE row is a real share of 0%, so a row that rises from it
 * prints its points (R281; before R281 that row was blank).
 *
 * The row is a DS `ChartLegendRow`, border-box at a fixed 20px, and its own
 * TRAP says padding past 2px clips the label. So this stays on ONE line beside
 * the count. The two-line `NewPercentageBadge` the metric cards use cannot fit
 * here, and only its arrow is borrowed.
 */
const LegendDelta = ({
	points,
	worse
}: {
	/** R281: percentage points of the share, or null when there is no earlier period. */
	points: number | null
	worse: boolean
}) => {
	// R151 (audit 2026-08-28): the chip used to unmount when the earlier period
	// went away, so a range switch that took it away cut the chip with no exit.
	// The chip stays mounted and fades; `last` holds the final real number
	// through the exit.
	const last = useRef<number | null>(null)
	if (points !== null) last.current = points
	const shown = points ?? last.current
	const hidden = points === null
	// R151 (audit 2026-08-28): `>=` counted an unchanged count as a rise, so a
	// Spam trap row at 1 vs 1 drew a red up arrow beside `0.0%`.
	// R316 (Gustavo, 11 Sep): "0.0% with no growth indicator, just a straight
	// line (still purple color tho)". Equal draws a flat line in the brand tone
	// where the arrow sits, the same chip the forms table draws since R313.
	const same = shown === null || shown === 0
	const up = shown !== null && shown > 0
	const Arrow = same ? Minus : up ? FaArrowTrendUp : FaArrowTrendDown
	const tone =
		same || !worse
			? // R178 (Esteban, 1 Sep): "a darker brand purple". --t-purple (#918AFF)
		// read too light beside the table's DS Change (#4435D0). --t-light-purple
		// is the darker brand indigo (#5E54FD light / #b7b2ff dark), theme-aware.
			  'text-t-light-purple'
			: // R296 (dark audit, pin #92): --t-danger never flips and read 4.7:1 on
		// the dark tray. --fs-delta-bad-ink keeps the #F04438 literal in light
		// and takes the 300-weight red in dark. --t-trend-up passes both (6:1).
			up
		? 'text-[rgb(var(--fs-delta-bad-ink))]'
		: 'text-t-trend-up'
	return (
		<span
			// NO colour transition here, and that is deliberate. `transition-colors`
			// left the paint STUCK on the old tone after a range switch: measured on
			// :3060, two rows carried the identical class `text-t-danger`, the
			// identical `--t-danger` value and no inline style, yet one painted red
			// and the other green, and it never caught up. Setting
			// `style.transition = 'none'` snapped every row right in the same frame.
			// A rising spam row painted green says the opposite of the truth, so the
			// tone lands with the number. The fade below (R151) is opacity ONLY:
			// `transition-opacity` never touches the colour.
			className={`flex shrink-0 items-center gap-1 transition-opacity duration-200 motion-reduce:transition-none ${
				hidden ? 'opacity-0' : 'opacity-100'
			} ${tone}`}
			aria-hidden={hidden || undefined}
			// R281: no `title` here any more. The legend head names the column
			// ("vs previous period", lowercase since R308) and carries the sentence
			// in a DS Tooltip, ONE per legend, so a row's own hover stays free for
			// the chart highlight.
		>
			<Arrow className="h-3 w-3 shrink-0" aria-hidden="true" />
			{/* R116, at his ask ("the numbers have too much spacing with the arrow
			    icons"). The number used to hold a fixed 46px and sit right-aligned
			    inside it, so the ARROW would land on the same x in every row.

			    That box was wider than the digits, so the 4px flex gap never drew
			    as 4px. Measured on :3060: the real gap ran 13.6px on a `17.3%` row
			    and 21.4px on a `4.5%` row. So it was BOTH too wide and ragged, next
			    to a 12px icon.

			    You cannot align the arrow's left edge and the number's right edge
			    at the same time when the number changes width. One of the two must
			    give. The arrow gives, because it belongs to its own number: an icon
			    that drifts away from the digits it describes reads as broken, and
			    an arrow that starts 8px further along does not.

			    The numbers still line up on the right, because the slot around this
			    chip (`LegendRow`, width from the legend head) right-aligns the whole
			    pair. */}
			{/* R225 (Gustavo, 2 Sep, pin ked64q): "Add a + or - sign on the growth
			    symbols", his example `↘ -21.5%`. The arrow stays and the sign joins
			    it, so the direction survives without the colour and without the
			    glyph: in a copy, in a screen reader, on a screen that cannot tell
			    the purple from the green. An unchanged share prints `0.0 p.p.`. */}
			<span>{shown === null ? null : deltaText(shown)}</span>
		</span>
	)
}

/**
 * One legend row. Its own component so each count owns a tween hook.
 *
 * R108: the metric slot carries three things now. The count, its share of the
 * period, and the change against the period before. R301: the three ride as
 * `values`, one DS cell each, and the DS lines the cells up under the head.
 *
 * The delta reads the SETTLED count, not the tweened one. A percentage that
 * counted up beside a number that was also counting up read as two clocks
 * disagreeing.
 */
const LegendRow = ({
	label,
	color,
	value,
	total,
	prev,
	prevTotal,
	worse,
	delta,
	hi,
	onHi
}: {
	label: string
	color: string
	value: number
	total: number
	prev?: number
	/** R281: the earlier period's total. No total, no share, so no delta prints. */
	prevTotal?: number
	worse: boolean
	/** R210: the legend prints a change column, so this row fills its cell. */
	delta: boolean
} & HiProps) => {
	const shown = useTweenedNumber(value)
	/* R208 (Esteban, 1 Sep): "Make these labels into columns too". The count and
	   the share used to ride in ONE string, so only the share's right edge lined
	   up and every count sat at a different x. Each number holds its own
	   right-aligned slot since then.

	   R281 (Esteban, 4 Sep): the slots took the widths the legend head measured
	   (`useLegendCols`), so the head's words and the rows' numbers ended on the
	   same x. The parentheses R225 put around the share are gone: the head names
	   the column.

	   R301 (Esteban, 11 Sep): the slots are DS cells. `ChartLegendRow.values`
	   takes one node per value column, and the DS `ChartLegend` measures every
	   column from its widest cell, head included, and follows the web font when
	   it swaps in. The row prints exactly as many cells as the head names: a
	   row with no earlier count keeps an EMPTY delta cell (the chip fades out
	   and holds its last number, R151), so its count and share never slide into
	   the delta column. */
	const points =
		prev === undefined || !prevTotal
			? null
			: sharePoints(value, total, prev, prevTotal)
	const values = [
		formatCount(Math.round(shown)),
		formatShare(shown, total),
		...(delta
			? [<LegendDelta key="delta" points={points} worse={worse} />]
			: [])
	]
	const dim = hi !== null && hi !== label
	return (
		<ChartLegendRow
			color={color}
			label={label}
			values={values}
			interactive
			dim={dim}
			onMouseEnter={() => onHi(label)}
			onMouseLeave={() => onHi(null)}
		/>
	)
}

/**
 * Legend for an outcome set, under ONE chart.
 *
 * R84 put the rows in the DS `ChartLegend`: three rows, half of a fourth, and
 * a scroll for the rest. R127 kept that box after R125 had removed it, because
 * seven rows in one column made the panel too tall.
 *
 * R181 (Esteban, 1 Sep): the rows are plain labels again, with no frame and no
 * scroll, and a long set stands in TWO columns side by side. Two columns of
 * four rows are 98px, the exact height the old scroll zone reserved, so the
 * panel does not grow and every row is readable without a wheel.
 *
 * R210 (Esteban, 1 Sep): BOTH chart legends stand in two columns, whatever the
 * row count. R181 had kept the bot set in one column, because its labels run to
 * 161px and a half column left only 69px. The room came out of the numbers
 * instead: the share dropped its parentheses, both slots came down to 34px, and
 * the bot legend gave up its delta column. A 277px column then pays 22px of row
 * chrome, 16px of row gap and 72px of numbers, which leaves 167px for a 161px
 * label. The delta needs 52px more and no legend has them, so a set with wide
 * labels prints the count and the share only.
 *
 * R229 (Esteban, 2 Sep): "make the labels shorter somehow so it's actually one
 * line". R227 had put the bot legend in ONE column and R228 had stacked each
 * row's numbers under its label, because "Suspicious visitor, CAPTCHA
 * disabled" is 204px and R225's 154px of numbers left a 277px half column
 * 97px short. He rejected both. The bot labels are one word each now, 61px at
 * the widest (`BOT_OUTCOMES`), so both legends share ONE layout: two columns,
 * one line a row, count, share and delta beside the label. A one-line row
 * pays 22px of row chrome, a 16px gap and 154px of numbers; the tray at 1440
 * leaves 67px for the label and his 1512 screen 85px, so no label is cut.
 *
 * The block spans the full column on purpose. A capped, centred legend under a
 * plot that runs edge to edge was rejected on Protection Analytics (2026-08-20),
 * and the DS says so in `ChartLegend` TRAP 3.
 *
 * R191: the side and bottom gutters left this block. Each chart now sits in a
 * DS `StatCard`, and the card already pays 12px on every side. The caller adds
 * a gutter back only where there is no card (the held verdict pair below).
 *
 * R281 (Esteban, 4 Sep): both legends stand in ONE column again, inside the DS
 * `ChartLegend` scroll box R181 took out (three rows, half of a fourth, a wheel
 * for the rest), with one head row above the box that names the category and
 * the three number columns. The two columns R210 and R229 kept paid for
 * themselves in label room; the box gives the room back, because a row has the
 * full card width. The change column reads percentage points of the share now,
 * and the head carries the sentence that used to sit in the row's `title`.
 *
 * R301 (Esteban, 11 Sep, for Gustavo, "make this sort of like a table"): the
 * head row and the number columns are the DS's own. `ChartLegend` grew a
 * table mode (DS `feat/chart-legend-columns`, 0.40.0): `columns` on the box
 * names the head, `values` on each row fills the cells, the head stands above
 * the scroll box so it never scrolls away, one 1px divider runs between the
 * columns from the head to the floor, and the DS measures every column from
 * its widest cell, head included. The hand-rolled head and column hook R281
 * built here are gone, with the two TRAPs they patched: the DS sets the block
 * height itself (zone plus head) and its framed box carries one border class
 * at a time. The head text is the DS table head token (12px semibold), where
 * R281 had set 14px by hand. Each legend is 4px of top gutter and the 125px
 * block (27px head, 98px zone): 129px, the same in both cards, so the two
 * stay level.
 */
/** R84: three full rows show, then half of a fourth, and the rest scroll. */
const LEGEND_ROWS = 3

/**
 * R281: the sentence behind the (i) on the change head, ONE per legend, so a
 * row's own hover stays free for the chart highlight (see `LegendDelta`).
 *
 * R338 (Gustavo, 15 Sep): "just noticed, this tooltip should say 'Valid' not
 * 'Normal'". One constant serves BOTH legends, and the two name their rows
 * differently: Email Validation says Valid, Bot Detection says Normal, so an
 * example with one row name was wrong on the other card. He gave the new text
 * and said "Let's adjust it to that tooltip text". It carries no row name, so
 * the sentence is now true on both cards, and it names the column the change
 * belongs to.
 */
const DELTA_HEAD_INFO =
	'Percentage-point (p.p.) change in “% of total.” Shows whether good/bad submissions are becoming a larger or smaller share of total submissions.'

const OutcomeLegend = ({
	defs,
	category,
	count,
	totals,
	prev,
	delta = true,
	hi,
	onHi
}: {
	defs: OutcomeDef[]
	/** R281: the head over the label column, what the rows are. */
	category: string
	/** R281: the head over the count column, what the bars count. */
	count: string
	/** R210: print the change against the earlier period. */
	delta?: boolean
	totals: Record<string, number>
	/**
	 * R108: the same totals for the period before this one. Leave it out and
	 * no row prints a delta, which is what the held verdict legend below does:
	 * it has no earlier period to compare against, so it claims none.
	 */
	prev?: Record<string, number>
} & HiProps) => {
	const showDelta = delta && prev !== undefined
	// R301: the first column is the label column; the DS draws no divider before
	// it. The change column, and its (i), stand only when the rows have an
	// earlier period to compare against.
	const columns: ChartLegendColumn[] = [
		{ label: category },
		{ label: count },
		{ label: '% of total' },
		// R308 (Esteban, 11 Sep): "make the 'Vs previous period' label on top of
		// the pp we just changed also all lowercase". The head now matches the
		// page tile's "vs previous period" title and the `p.p.` unit (R305).
		...(showDelta
			? [{ label: 'vs previous period', info: DELTA_HEAD_INFO }]
			: [])
	]
	return (
		// R140 (Esteban, 2026-08-28): the legend sits right under the chart, not at
		// the foot of the column. `mt-auto` used to push it down, and a filtered
		// legend of one row then stood at the bottom with dead space above it. The
		// two columns still share one height, so the free space now sits under the
		// shorter legend, where it reads as room, not as a gap in the middle.
		<div className="pt-1 form-outcome-legend">
			<ChartLegend
				visibleRows={LEGEND_ROWS}
				// `framed`, not the `'auto'` default: a filtered legend of one to
				// three rows keeps its frame, its fill and its 98px zone, so the two
				// cards stay level and a filter never makes the box blink in and out.
				framed
				columns={columns}
				// No `style` here, on purpose. `ChartLegend` spreads `...rest` AFTER
				// its own `style`, so a caller style would replace the block height
				// the DS computes (zone plus head) and the box would collapse.
			>
				{defs.map((def) => (
					<LegendRow
						key={def.key}
						label={def.label}
						color={def.color}
						value={totals[def.key] ?? 0}
						total={totals.total ?? 0}
						prev={prev?.[def.key]}
						prevTotal={prev?.total}
						worse={isWorseWhenUp(def)}
						delta={showDelta}
						hi={hi}
						onHi={onHi}
					/>
				))}
			</ChartLegend>
		</div>
	)
}

const VERDICT_DEFS: OutcomeDef[] = [
	{ key: 'allow', label: 'Allow', color: VERDICT_COLORS.allow },
	{ key: 'block', label: 'Block', color: VERDICT_COLORS.block },
	{ key: 'challenge', label: 'Challenge', color: VERDICT_COLORS.challenge }
]

const VerdictLegend = ({
	totals,
	hi,
	onHi
}: { totals: VerdictTotals } & HiProps) => (
	// R191: `OutcomeLegend` lost its own gutter to the card. This pair has no
	// card, so the gutter comes back here and the held draw stays as it was.
	<div className="px-3 pb-3">
		<OutcomeLegend
			defs={VERDICT_DEFS}
			category="Verdict"
			count="Submissions"
			totals={{
				allow: totals.allow,
				block: totals.block,
				challenge: totals.challenge,
				total: totals.total
			}}
			hi={hi}
			onHi={onHi}
		/>
	</div>
)

/**
 * Daily verdict shares as three unstacked lines. The bed draws the grid.
 *
 * R53: the trend READS the highlight and never sets it. `enableSlices="x"`
 * hands the pointer one card for the whole day, so no single series is ever
 * under the cursor and there is nothing for the chart to raise. The legend
 * under it is the only side that sets `hi`.
 */
const VerdictTrend = ({
	points,
	hi
}: { points: VerdictPoint[] } & Pick<HiProps, 'hi'>) => {
	const reduceMotion = usePrefersReducedMotion()
	const wrapRef = useRef<HTMLDivElement>(null)
	const data = useMemo(() => {
		const share = (pick: (p: VerdictPoint) => number) =>
			points.map((p) => {
				const total = p.allow + p.block + p.challenge
				return { x: p.day, y: total === 0 ? 0 : (pick(p) / total) * 100 }
			})
		// Challenge first so the darker allow line paints on top of it.
		return [
			{ id: 'Challenge', data: share((p) => p.challenge) },
			{ id: 'Block', data: share((p) => p.block) },
			{ id: 'Allow', data: share((p) => p.allow) }
		]
	}, [points])
	// R53: nivo owns the line and the area paths, so there is no element to put
	// a class on. The Protection analytics donut hit the same wall and answered
	// it the same way: hold the plot in a ref and paint `opacity` on the paths
	// after each render. The transition lives in `form-spam-page.css`, because a
	// style written here would fight the one written on the next pass.
	//
	// Paint order, and the reason the modulo below is right: nivo draws every
	// layer back to front, so `lines.slice(0).reverse()` runs the series in
	// reverse. `enableArea` is on, so the plot holds one area path per series
	// and then one line path per series, each group in that same reverse order.
	// The guard bails if that ever stops being true, and the plot then reads
	// exactly as it did before this round.
	useEffect(() => {
		const paths = wrapRef.current?.querySelectorAll<SVGPathElement>('svg path')
		if (!paths) return
		const paintOrder = [...data].reverse().map((serie) => String(serie.id))
		if (paths.length % paintOrder.length !== 0) return
		paths.forEach((path, index) => {
			const id = paintOrder[index % paintOrder.length]
			path.style.opacity =
				hi === null ? '' : id === hi ? '1' : String(DIM_OPACITY)
		})
	}, [hi, data])

	// Zero margins: the bed aligns the plot edges with the 100% and 0%
	// hairline centers, so any margin lifts the area off the 0% line.
	const plot = (
		<ResponsiveLine
			data={data}
			margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
			xScale={{ type: 'point' }}
			yScale={{ type: 'linear', min: 0, max: 100 }}
			curve="monotoneX"
			colors={[
				VERDICT_COLORS.challenge,
				VERDICT_COLORS.block,
				VERDICT_COLORS.allow
			]}
			lineWidth={2}
			enablePoints={false}
			enableGridX={false}
			enableGridY={false}
			axisTop={null}
			axisRight={null}
			axisBottom={null}
			axisLeft={null}
			enableArea
			areaOpacity={0.16}
			enableSlices="x"
			// R131 (Gustavo, pin 370ie1 follow-up): the card is the DS
			// ChartTooltip. nivo owns the hover anchoring and ChartTip owns the
			// portal, so the DS parts only draw the surface, the title, the rows
			// and the rule. The old hand-rolled copy drew its own `border-white/15`
			// rule, which this app's Tailwind drops, so it painted full white.
			sliceTooltip={({ slice }) => {
				const day = String(slice.points[0]?.data.x ?? '')
				const point = points.find((p) => p.day === day)
				if (!point) return null
				const total = point.allow + point.block + point.challenge
				const rows = [
					{
						key: 'allow',
						label: 'Allow',
						color: VERDICT_COLORS.allow,
						value: point.allow
					},
					{
						key: 'block',
						label: 'Block',
						color: VERDICT_COLORS.block,
						value: point.block
					},
					{
						key: 'challenge',
						label: 'Challenge',
						color: VERDICT_COLORS.challenge,
						value: point.challenge
					}
				]
				return (
					<ChartTip>
						<ChartTooltip className="form-spam-chart-tip whitespace-nowrap">
							<ChartTooltipTitle>{day}</ChartTooltipTitle>
							<ChartTooltipList>
								{rows.map((row) => (
									<ChartTooltipRow
										key={row.key}
										label={
											<>
												<ChartTooltipSwatch color={row.color} />
												{row.label}
											</>
										}
										value={`${formatCount(row.value)} (${formatShare(
											row.value,
											total
										)})`}
									/>
								))}
							</ChartTooltipList>
						</ChartTooltip>
					</ChartTip>
				)
			}}
			theme={{
				crosshair: {
					line: {
						stroke: 'rgb(var(--cg-text-tertiary))',
						strokeWidth: 1,
						strokeDasharray: '3 3'
					}
				},
			// nivo wraps the slice card in its own themed box: strip that box
			// so the dark card above is the only surface.
				tooltip: {
					container: {
						background: 'transparent',
						borderRadius: 0,
						boxShadow: 'none',
						padding: 0
					}
				}
			}}
			// Use a named preset supported by the installed Nivo chart wrapper.
			animate={!reduceMotion}
			motionConfig="gentle"
		/>
	)
	return (
		<div ref={wrapRef} className="form-spam-plot-hover h-full w-full">
			{plot}
		</div>
	)
}

/** The top cap radius, in px. The bars are 8px wide, so 2px reads as a cap. */
const BAR_CAP = 2

type OutcomeRow = Record<string, string | number>

/** A rectangle with ONLY its two top corners rounded. */
const topCapPath = (rawWidth: number, rawHeight: number) => {
	const w = Math.max(rawWidth, 0)
	const h = Math.max(rawHeight, 0)
	const r = Math.min(BAR_CAP, w / 2, h)
	return [
		`M0,${h}`,
		`L0,${r}`,
		`A${r},${r} 0 0 1 ${r},0`,
		`L${w - r},0`,
		`A${r},${r} 0 0 1 ${w},${r}`,
		`L${w},${h}`,
		'Z'
	].join(' ')
}

/**
 * R87: the day must read as ONE bar with a rounded top, not as a column of
 * separate pills.
 *
 * `borderRadius` on `ResponsiveBar` rounds all four corners of EVERY segment.
 * A seven-outcome day therefore drew seven capsules, and each rounded join
 * left a pale notch that read as a gap. Nivo 0.83 has no per-segment and no
 * per-corner radius axis, so a custom `barComponent` is the only way through.
 *
 * The body below is `@nivo/bar/src/BarItem.tsx` (0.83.0), copied. Two branches
 * are dropped because this chart switches them off: the label (`enableLabel`
 * is false) and the keyboard focus handlers (`isFocusable` is false). The
 * `<rect>` becomes a top-capped `<path>` on the topmost segment.
 *
 * The topmost key rides on the ROW, as `__top`, and not on a closure over
 * `defs`. `barComponent` must keep ONE identity for the life of the chart. A
 * component minted inside the render remounts every bar on each pass, and
 * react-spring then cuts the 7/30 flip instead of morphing it.
 */
const OutcomeBar = ({
	bar: { data, ...bar },
	style: { borderColor, color, height, transform, width },
	borderWidth,
	isInteractive,
	onClick,
	onMouseEnter,
	onMouseLeave,
	tooltip
}: BarItemProps<OutcomeRow>) => {
	const { showTooltipFromEvent, hideTooltip } = useTooltip()
	// R53: the highlighted key arrives through a context, never through a
	// closure, for the identity reason in the note above.
	const hiKey = useContext(HiKeyContext)

	const renderTooltip = useMemo(
		() => () => createElement(tooltip, { ...bar, ...data }),
		[tooltip, bar, data]
	)
	const handleClick = useCallback(
		(event: React.MouseEvent<SVGElement>) => {
			onClick?.({ color: bar.color, ...data }, event as never)
		},
		[bar, data, onClick]
	)
	const handleTooltip = useCallback(
		(event: React.MouseEvent<SVGElement>) =>
			showTooltipFromEvent(renderTooltip(), event),
		[showTooltipFromEvent, renderTooltip]
	)
	const handleMouseEnter = useCallback(
		(event: React.MouseEvent<SVGElement>) => {
			onMouseEnter?.(data, event as never)
			showTooltipFromEvent(renderTooltip(), event)
		},
		[data, onMouseEnter, showTooltipFromEvent, renderTooltip]
	)
	const handleMouseLeave = useCallback(
		(event: React.MouseEvent<SVGElement>) => {
			onMouseLeave?.(data, event as never)
			hideTooltip()
		},
		[data, hideTooltip, onMouseLeave]
	)

	const shared = {
		fill: data.fill ?? color,
		strokeWidth: borderWidth,
		stroke: borderColor,
		// R53: the segment steps back when another outcome holds the highlight.
		// `style`, not the `opacity` attribute, because a CSS transition only
		// carries a property the element really sets.
		className: 'transition-opacity duration-150 motion-reduce:transition-none',
		style: {
			opacity: hiKey !== null && hiKey !== String(data.id) ? DIM_OPACITY : 1
		},
		onMouseEnter: isInteractive ? handleMouseEnter : undefined,
		onMouseMove: isInteractive ? handleTooltip : undefined,
		onMouseLeave: isInteractive ? handleMouseLeave : undefined,
		onClick: isInteractive ? handleClick : undefined
	}

	return (
		<animated.g transform={transform}>
			{data.data.__top === data.id ? (
				<animated.path
					d={to([width, height], (w, h) => topCapPath(w, h))}
					{...shared}
				/>
			) : (
				<animated.rect
					width={to(width, (value) => Math.max(value, 0))}
					height={to(height, (value) => Math.max(value, 0))}
					{...shared}
				/>
			)}
		</animated.g>
	)
}

/**
 * Daily counts as stacked bars, one segment per outcome (Round 30). The bed
 * draws the grid and the count labels; `maxValue` comes from the same
 * countRows() call that made the labels, so the bars and the axis agree.
 * Keys stack first-at-the-bottom, so the green base outcome sits on the 0%
 * line and the failure segments ride on top of it.
 */
const OutcomeBars = ({
	points,
	defs,
	maxValue,
	hi,
	onHi
}: {
	points: OutcomePoint[]
	defs: OutcomeDef[]
	maxValue: number
} & HiProps) => {
	const reduceMotion = usePrefersReducedMotion()
	const dark = useIsDarkTheme()
	// `__top` names the topmost segment of the day: the LAST key with a count
	// above zero, because the keys stack first-at-the-bottom. A key that reads
	// zero draws a zero-height bar, so it must never take the cap.
	const data = useMemo<OutcomeRow[]>(
		() =>
			points.map((p) => {
				const top = [...defs]
					.reverse()
					.find((def) => (p.counts[def.key] ?? 0) > 0)
				return { day: p.day, ...p.counts, __top: top ? top.key : '' }
			}),
		[points, defs]
	)
	const colorByKey = useMemo(
		() => Object.fromEntries(defs.map((d) => [d.key, d.color])),
		[defs]
	)
	// R53: the panel holds the highlight as a LABEL, and this chart alone stacks
	// by key, so it translates in both directions.
	const labelByKey = useMemo(
		() => Object.fromEntries(defs.map((d) => [d.key, d.label])),
		[defs]
	)
	const hiKey = useMemo(
		() => (hi === null ? null : defs.find((d) => d.label === hi)?.key ?? null),
		[defs, hi]
	)
	const plot = (
		<ResponsiveBar
			data={data}
			keys={defs.map((d) => d.key)}
			indexBy="day"
			margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
			padding={0.35}
			valueScale={{ type: 'linear', min: 0, max: maxValue }}
			colors={({ id }) =>
				colorByKey[String(id)] ??
				(dark ? '#2a253f' /* dark --cg-subtle */ : '#ededed')
			}
			barComponent={OutcomeBar}
			// R53: the pointer over a segment raises that outcome in the legend
			// too. `datum.id` is the stack key, so it maps back to the label.
			onMouseEnter={(datum) => onHi(labelByKey[String(datum.id)] ?? null)}
			onMouseLeave={() => onHi(null)}
			enableLabel={false}
			enableGridX={false}
			enableGridY={false}
			axisTop={null}
			axisRight={null}
			axisBottom={null}
			axisLeft={null}
			// One card for the whole day, whichever segment is under the
			// pointer: per-segment cards would flicker through five or seven
			// variants on a single sweep across the chart.
			tooltip={({ indexValue, data: row }) => {
				const total = defs.reduce(
					(sum, def) => sum + (Number(row[def.key]) || 0),
					0
				)
				return (
					<ChartTip>
						{/* R131: the DS ChartTooltip, same as the slice card above. */}
						<ChartTooltip className="form-spam-chart-tip whitespace-nowrap">
							<ChartTooltipTitle>{String(indexValue)}</ChartTooltipTitle>
							<ChartTooltipList>
								{defs.map((def) => {
									const value = Number(row[def.key]) || 0
									return (
										<ChartTooltipRow
											key={def.key}
											label={
												<>
													<ChartTooltipSwatch color={def.color} />
													{def.label}
												</>
											}
											value={`${formatCount(value)} (${formatShare(
												value,
												total
											)})`}
										/>
									)
								})}
								{/* R125 (Gustavo, pin 370ie1): the day's total closes the
								    card, so the shares above it have a denominator. R131: the
								    DS `total` row, so the label sits at the left edge and the
								    rule takes `--cg-tooltip-divider`. */}
								<ChartTooltipRow
									total
									label="Total"
									value={`${formatCount(total)} (${formatShare(total, total)})`}
								/>
							</ChartTooltipList>
						</ChartTooltip>
					</ChartTip>
				)
			}}
			theme={{
				tooltip: {
					container: {
						background: 'transparent',
						borderRadius: 0,
						boxShadow: 'none',
						padding: 0
					}
				}
			}}
			animate={!reduceMotion}
			motionConfig="gentle"
		/>
	)
	return <HiKeyContext.Provider value={hiKey}>{plot}</HiKeyContext.Provider>
}

/**
 * Range totals as one horizontal 100% bar (R168; Gustavo, pin p_mtdgpoc2).
 * The donut this replaces said each share as an angle while the legend under
 * it said the same share as a number, so the ring carried nothing the card
 * did not already say. Length reads straighter than angle, and the day
 * stacks beside this view already speak in bar lengths. The box stays 208px,
 * the exact height of the trend's ChartBed, so the columns stay level.
 *
 * The items arrive in def order and a dropped outcome arrives at value 0
 * instead of leaving the array (see the call site): its segment then plays
 * its exit by width, not by an unmount's hard cut. A zero segment also
 * gives up its 2px gap, or the gaps would read as notches cut into the bar
 * (the donut had the same bug with `padAngle` on zero slices).
 *
 * Generic over its slices (Round 30): the verdict mix and the per-layer
 * outcome mixes are this same bar with different items.
 */
const OutcomeMix = ({
	items,
	total,
	hi,
	onHi
}: {
	items: { label: string; color: string; value: number }[]
	total: number
} & HiProps) => {
	const empty = total === 0
	const shownTotal = useTweenedNumber(total)
	const [tip, setTip] = useState<string | null>(null)
	const tipItem = items.find((item) => item.label === tip)
	// The last live segment carries no gap, or the bar would end on a 2px
	// notch just inside the rounded clip.
	const lastLive = items.reduce(
		(acc, item, index) => (item.value > 0 ? index : acc),
		-1
	)
	return (
		<div className="flex h-[208px] w-full flex-col items-center justify-center gap-5">
			<div className="flex flex-col items-center">
				<span className="text-xl font-semibold leading-7 text-[rgb(var(--cg-text-primary))]">
					{formatCount(Math.round(shownTotal))}
				</span>
				<span className="text-[11px] leading-4 text-[rgb(var(--cg-text-secondary))]">
					Total
				</span>
			</div>
			{/* The legend under the card names every outcome and its share, so
			    the bar itself is decoration to a screen reader. */}
			<div className="flex h-4 w-full overflow-hidden rounded-full" aria-hidden>
				{empty ? (
					<div className="h-full w-full bg-[rgb(var(--cg-subtle))]" />
				) : (
					items.map((item, index) => (
						<div
							key={item.label}
							className="h-full rounded-[3px] transition-[width,margin-right,opacity] duration-200 motion-reduce:transition-none"
							style={{
								width: `${(item.value / total) * 100}%`,
								marginRight: item.value > 0 && index !== lastLive ? 2 : 0,
								backgroundColor: item.color,
								// R53's raise-the-legend-row hover, without the donut's
								// querySelector reach: this bar owns its DOM, so the
								// dim is a plain style.
								opacity: hi === null || hi === item.label ? 1 : DIM_OPACITY
							}}
							onMouseEnter={() => {
								onHi(item.label)
								setTip(item.label)
							}}
							onMouseLeave={() => {
								onHi(null)
								setTip(null)
							}}
						/>
					))
				)}
			</div>
			{tipItem && tipItem.value > 0 ? (
				<ChartTip>
					{/* R131: the DS ChartTooltip, one row, no title. `ChartTip`
					    pins it to the pointer, the same card the donut raised. */}
					<ChartTooltip className="form-spam-chart-tip whitespace-nowrap">
						<ChartTooltipList>
							<ChartTooltipRow
								label={
									<>
										<ChartTooltipSwatch color={tipItem.color} />
										{tipItem.label}
									</>
								}
								value={`${formatCount(tipItem.value)} (${formatShare(
									tipItem.value,
									total
								)})`}
							/>
						</ChartTooltipList>
					</ChartTooltip>
				</ChartTip>
			) : null}
		</div>
	)
}
const VerdictMix = ({
	totals,
	hi,
	onHi
}: { totals: VerdictTotals } & HiProps) => (
	<OutcomeMix
		items={VERDICT_DEFS.map((def) => ({
			label: def.label,
			color: def.color,
			value: totals[def.key as keyof VerdictTotals]
		}))}
		total={totals.total}
		hi={hi}
		onHi={onHi}
	/>
)

/** Everything one layer hands its column. */
type OutcomeColumnData = {
	points: OutcomePoint[]
	defs: OutcomeDef[]
	bed: { max: number; rows: string[] }
	totals: Record<string, number>
	/** R108: the same totals one period earlier. The legend prints the change. */
	prev: Record<string, number>
}

/**
 * One layer's column: a head, one chart, and the legend under it. The two
 * layers differ only in their copy and their outcome set, so they share this
 * body instead of repeating it.
 *
 * R84: the chart carries a legend. Five and seven outcomes do not fit a row of
 * labels, so these two views had none at all and the names lived only in the
 * hover tooltip. The DS `ChartLegend` scrolls, so the count no longer decides.
 */
/**
 * R125 (Gustavo, pin xpjrjh): which bars get a date under them. R129: at
 * seven days every bar gets one. The 7 Days view left only the first and
 * the last day, which read as no axis at all (Esteban, 2026-08-27). Above
 * ten bars a label per bar overlaps, so the thirty day view keeps the
 * first, every seventh, and the last. A seventh that lands within four
 * bars of the last one is skipped, or the two labels touch.
 */
const DATE_EVERY_BAR_UP_TO = 10
const showsDate = (index: number, count: number) =>
	count <= DATE_EVERY_BAR_UP_TO ||
	index === count - 1 ||
	(index % 7 === 0 && count - 1 - index >= 4)

/**
 * R134 (Gustavo, pin 7n8gri; Esteban, 2026-08-27): one filter per chart, on
 * the chart's own head, so the reader can keep one outcome (say, Invalid) and
 * drop the rest. An empty selection means every outcome, the way the table's
 * Filters read it.
 *
 * R139 (Esteban, 2026-08-28): a plain dropdown, not the two-pane filter menu,
 * and no count on the button. The trigger is the same square icon button the
 * Analytics tiles carry. The panel is the DS `MenuList` of `ListItem` rows.
 * R180 (Esteban, 1 Sep): a kept outcome carries a DS `Checkbox` in the row's
 * LEADING slot, not the brand check in the trailing slot. The box says the row
 * is selectable before the pointer arrives, which the trailing check could not.
 * The pattern is the ad-account dropdown's: the box is a real input, the
 * wrapper stops the press so the row does not toggle twice, and the row's own
 * click still toggles. R168: the top row reads Select all, and while nothing is
 * kept it carries the check and so does every row under it, because every
 * outcome really is on. Press a row from that state and the chart drops that one and
 * keeps the rest, which is what the checks promise. Select all puts the chart
 * back. A row toggles and the menu stays open, so several
 * outcomes can be kept in one visit (the DS `Dropdown` closes on every pick
 * and holds one value, so it cannot be the panel here). The panel hangs off
 * the button through the DS anchor and popover-motion hooks, in a portal, so
 * the scrolling drawer body cannot clip it.
 */
const FILTER_GAP = 4

const OutcomeFilter = ({
	title,
	defs,
	selected,
	onChange
}: {
	title: string
	defs: OutcomeDef[]
	selected: string[]
	onChange: (next: string[]) => void
}) => {
	const [open, setOpen] = useState(false)
	const anchorRef = useRef<HTMLSpanElement | null>(null)
	const panelRef = useRef<HTMLDivElement | null>(null)
	const motion = usePopoverMotion(open, { transformOrigin: 'top right' })
	// The panel sits under the button's right edge; `right` (not `left`) so a
	// max-content panel resolves its width against the free side.
	const place = useCallback((strategy: AnchorStrategy) => {
		const anchor = anchorRef.current
		const panel = panelRef.current
		if (!anchor || !panel) return
		const rect = anchor.getBoundingClientRect()
		panel.style.top = `${anchoredTop(rect.bottom + FILTER_GAP, strategy)}px`
		panel.style.right = `${anchoredRight(
			window.innerWidth - rect.right,
			strategy
		)}px`
	}, [])
	const strategy = useAnchoredPlacement({
		active: motion.mounted,
		anchorRef,
		panelRef,
		place
	})

	// A press outside the button and the panel, or Escape, closes the menu.
	// The button itself is inside the anchor, so its own click does the toggle.
	useEffect(() => {
		if (!open) return
		const onDown = (event: MouseEvent) => {
			const target = event.target as Node
			if (
				anchorRef.current?.contains(target) ||
				panelRef.current?.contains(target)
			)
				return
			setOpen(false)
		}
		// R151 (audit 2026-08-28): the DS Sheet listens for Escape on `document`
		// too (bubble phase), so one press closed the menu AND the drawer. This
		// listener runs in the capture phase, ahead of the Sheet's, and stops
		// the event there: Escape owns the menu while the menu is open.
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return
			event.preventDefault()
			event.stopPropagation()
			setOpen(false)
		}
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onKey, true)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onKey, true)
		}
	}, [open])

	// R180: the empty selection still means every outcome, but a selection that
	// names every outcome means the same thing, and the boxes have to agree.
	// Keep every row and the old code left Select all UNCHECKED under seven
	// checked rows. `toggle` reads `all` too, and both branches drop one
	// outcome from the full set, so the reading is safe there as well.
	const all = selected.length === 0 || selected.length === defs.length
	// R168 (Esteban, 31 Aug): every row carries the check while `all` is on, so
	// a press on a checked row has to drop that one and keep the rest. The empty
	// selection still means every outcome, so the expansion happens here. Drop
	// the last kept outcome and the selection empties, which reads as all again:
	// a chart with no series has nothing to draw.
	const toggle = (key: string) => {
		if (all)
			return onChange(
				defs.filter((def) => def.key !== key).map((def) => def.key)
			)
		onChange(
			selected.includes(key)
				? selected.filter((k) => k !== key)
				: [...selected, key]
		)
	}

	return (
		<span ref={anchorRef} className="inline-flex">
			{/* R226 (Gustavo, 2 Sep, pin kef4v3): "Should we have a visual indicator of
			    when there's a filter applied (not all categories selected)? Maybe the
			    box border being purple?". While a filter is on, the trigger takes the
			    brand border and the brand glyph. `all` is the flag the menu reads, so
			    the button and Select all can never disagree. The border colour goes
			    in through the style attribute: the DS secondary variant sets its own
			    border colour, and an added class would tie with it. The label says how
			    many outcomes are shown, for a reader who cannot see the border. No
			    transition class of ours: the DS button already fades its colours over
			    150ms both ways (`cg-transition-colors cg-duration-150`), and the DS
			    stylesheet zeroes every `cg-` transition under reduced motion. */}
			<IconButton
				variant="secondary"
				size="sm"
				color={all ? undefined : 'brand'}
				style={all ? undefined : { borderColor: 'rgb(var(--cg-brand))' }}
				icon={<FilterLines />}
				aria-label={`Filter the ${title} chart by outcome${
					all ? '' : `, ${selected.length} of ${defs.length} shown`
				}`}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
			/>
			{motion.mounted
				? createPortal(
						<div
							ref={panelRef}
							style={{
								position: strategy,
								zIndex: zLayers.popover,
								...motion.style
							}}
						>
							<MenuList
								role="menu"
								aria-label={`${title} outcomes`}
								className="w-[240px]"
							>
								{/* R195 (Esteban, 1 Sep): while `all` is on, Select all has nothing to
								    do. Its press ran `onChange([])` onto a chart that already shows
								    every outcome, so the row lit purple on hover, took the press and
								    changed nothing. `disabled` is the DS answer: `listItemVariants`
								    ships `disabled:cg-opacity-50 disabled:cg-pointer-events-none` on
								    the default row, so the row dims, drops the hover fill and stops
								    the pointer. The box keeps the same `disabled`, so a keyboard user
								    cannot toggle a control inside a dead row. Drop one outcome and
								    the row comes back, because now it has work to do. */}
								<ListItem
									role="menuitemcheckbox"
									aria-checked={all}
									disabled={all}
									label="Select all"
									leadingControl={
										<span
											className="flex items-center"
											onClick={(event) => event.stopPropagation()}
										>
											<Checkbox
												size="sm"
												checked={all}
												disabled={all}
												onChange={() => onChange([])}
											/>
										</span>
									}
									onClick={() => onChange([])}
								/>
								<MenuListDivider />
								{defs.map((def) => {
									const kept = all || selected.includes(def.key)
									return (
										<ListItem
											key={def.key}
											role="menuitemcheckbox"
											aria-checked={kept}
											label={def.label}
											leadingControl={
												<span
													className="flex items-center"
													onClick={(event) => event.stopPropagation()}
												>
													<Checkbox
														size="sm"
														checked={kept}
														onChange={() => toggle(def.key)}
													/>
												</span>
											}
											onClick={() => toggle(def.key)}
										/>
									)
								})}
							</MenuList>
						</div>,
						document.body
					)
				: null}
		</span>
	)
}

/** The tallest day of a stack, counted over `defs` alone. */
const peakOf = (points: OutcomePoint[], defs: OutcomeDef[]) =>
	Math.max(
		1,
		...points.map((point) =>
			defs.reduce((sum, def) => sum + (point.counts[def.key] ?? 0), 0)
		)
	)

const OutcomeColumn = ({
	title,
	subtitle,
	axisLabel,
	legendCategory,
	filter,
	onFilter,
	points,
	defs,
	bed,
	totals,
	prev,
	loading = false
}: {
	loading?: boolean
	title: string
	/** R232 (Gustavo, pin #64): the head's second line, what the bars count and how they split. */
	subtitle: string
	/** R125 (Gustavo, pin mivax9): what the bars count. R128: rotated, left of the bed. */
	axisLabel: string
	/**
	 * R281: the head over the legend's label column, what the rows are. The
	 * count column takes `axisLabel`, because the legend counts what the bars
	 * count.
	 */
	legendCategory: string
	/** R134: the outcome keys this chart keeps. Empty keeps them all. */
	filter: string[]
	onFilter: (next: string[]) => void
} & OutcomeColumnData) => {
	// R53: one highlight per chart-legend PAIR, the way the Protection analytics
	// report scopes it. R102: the two columns now name DIFFERENT outcomes, and
	// the pointer sits in one column at a time, so a shared state would light
	// the column the reader is not looking at.
	const hi = useHighlight()
	// R134: the filter narrows `defs`, and the bars and the legend read
	// `defs`, so one narrowing reaches both. The bed follows the kept
	// outcomes, so a lone Invalid bar fills the
	// bed instead of sitting in the bottom tenth of the full stack's ladder.
	// The legend keeps the period total, so each row's share stays a fact
	// about the period and not about the filter.
	const shown = useMemo(
		() =>
			filter.length > 0 ? defs.filter((def) => filter.includes(def.key)) : defs,
		[defs, filter]
	)
	const shownBed = useMemo(
		() => (filter.length > 0 ? countRows(peakOf(points, shown)) : bed),
		[filter.length, points, shown, bed]
	)
	// R151: the date row swaps on the bar count (see the row below). The old
	// points are held for the exit, or the leaving row would already show the
	// new slots under the old key.
	const dateSwap = useSwapKey(String(points.length))
	const heldPoints = useRef(points)
	if (!dateSwap.leaving) heldPoints.current = points
	const datePoints = dateSwap.leaving ? heldPoints.current : points
	return (
		// R191 (Esteban, 2026-09-01): each chart is its own card now, the way the
		// dashboard draws its panels. The DS `StatCard` IS that card: its header
		// takes the icon, the title, the info trigger and the filter in the
		// `trailing` slot, so the old hand-rolled `ChartHead` is not needed here.
		// `size="small"` pays 12px on every side, which is the padding the chart
		// body and the legend used to pay themselves.
		//
		// R198 (Esteban, 1 Sep): no rule between the head and the bars. The card
		// edge already closes the head, and the bed draws its own gridlines, so a
		// third horizontal line said nothing. The DS carries the switch
		// (`StatCard.divider`, default true), so this is the prop, not an
		// override.
		<StatCard
			size="small"
			divider={false}
			className="min-w-0"
			icon={<BarChart01 className="h-4 w-4" />}
			title={title}
			infoTrigger={
				// The DS Tooltip, not the native title attribute: the native strip
				// needs a ~1s hover and reads as "no tooltip". R232: one row per
				// outcome, the full set and not the filtered one, because the
				// glossary explains the words and the filter picks the bars. Seven
				// rows stand ~270px tall and the heads sit near the top of the
				// page, so the tooltip opens downward; the DS Tooltip does not flip.
				// R269: the page CSS caps every lane tooltip at 240px; the glossary
				// is a list of definitions and keeps the DS 320 through the prop.
				<Tooltip
					content={<OutcomeGlossary defs={defs} />}
					position="bottom-start"
					maxWidth={320}
					portal
				>
					<span className="flex cursor-default">
						<InfoCircle className="h-3 w-3" />
					</span>
				</Tooltip>
			}
			trailing={
				<OutcomeFilter
					title={title}
					defs={defs}
					selected={filter}
					onChange={(next) => {
						// A highlight on a row that leaves the chart would dim the rest for
						// nothing, so the change drops it.
						hi.onHi(null)
						onFilter(next)
					}}
				/>
			}
		>
			{/* R232 (Gustavo, 3 Sep, pin #64): the head carries a second line, the
			    way the Analytics panel heads do ("Bad traffic rate" over "by
			    Campaign type"). The DS StatCard has no subtitle slot yet, so the
			    line is the card's first child, pulled up over the card's 16px gap
			    to sit right under the 32px head row, and indented 22px (16px icon
			    + 6px gap) so it starts under the title text. A `subtitle` prop on
			    the DS StatCard is the right home; noted for the DS.
			    R293 (Gustavo, 4 Sep, pin #71): pulled 4px further, into the head
			    row's bottom, so 3px stand between the title's line box and this
			    one. The 8px between the title and the info mark is the StatCard's
			    own `gap-s-8`, plus its 16px trigger box around the 12px glyph;
			    that half is the DS's to tighten, not a product override. */}
			<p className="-mt-5 pl-[22px] text-sm cg-text-text-secondary">
				{subtitle}
			</p>
			{/* The chart and its legend are ONE child of the card. The card puts
			    16px between its own children, and the legend belongs to the bars,
			    not a card gap away from them.

			    R207 (Esteban, 1 Sep): "Add divider between graphs and labels or at
			    least more spacing". The dates and the first legend row TOUCHED:
			    measured 0px between them. The legend's own 4px `pt-1` bought
			    nothing, because the date row hangs 4px below the chart block it
			    sits in, and the gap counts from the block. The gap here therefore
			    draws its full value as real air.

			    R211 (Esteban, 1 Sep, inspect edit): 12px was not enough. 16px, the
			    card's own child gap, so the legend sits off the dates the way the
			    head sits off the chart.

			    Spacing, not a rule: R198 took the header rule off this same card
			    because he said we do not need the dividers. */}
			{/* R303: `grow` on this wrapper and on the chart block hands the card's
			    spare height to the bars alone. `grow` and not `flex-1`: the basis
			    stays `auto`, so the block never shrinks under its 208px bed and a
			    short window scrolls the way it did. The legend, the dates and the
			    axis title keep their own size. */}
			{loading || !points.some((point) => shown.some((def) => (point.counts[def.key] ?? 0) > 0)) ? (
				<ChartPanelStatus mode={loading ? 'loading' : 'empty'} />
			) : (
			<div className="flex grow flex-col gap-4">
					<div className="flex grow gap-1">
						{/* R128 (Esteban, 2026-08-27): the axis title stands on the left
						    of the bed, rotated, the way the product `BarChart` draws its
						    nivo `axisLeft` legend (10px, gray, `legendPosition: middle`).
						    The bed has no nivo axis, so this gutter is the legend. The
						    bottom padding equals the date row (4px + 14px), so the title
						    centers on the bed, not on the bed plus the dates. R130: the
						    title and the dates took the DS `--cg-text-tertiary` token, one
						    step fainter than the #535255 tick numbers. R296 (dark audit,
						    pin #92) lifts them to text-secondary: tertiary read 5.2:1 on
						    the dark surface, secondary reads 8.5:1. */}
						<span className="flex shrink-0 items-center self-stretch pb-[18px]">
							<span
								className="whitespace-nowrap text-[10px] leading-[14px] text-[rgb(var(--cg-text-secondary))]"
							style={{
								writingMode: 'vertical-rl',
								transform: 'rotate(180deg)'
							}}
							>
								{axisLabel}
							</span>
						</span>
						<div className="flex min-w-0 flex-1 flex-col">
							<ChartBed rows={shownBed.rows}>
								<OutcomeBars
									points={points}
									defs={shown}
									maxValue={shownBed.max}
									{...hi}
								/>
							</ChartBed>
							{/* One slot per bar, the same 34px offset as the bed's plot
							    layer, so each date centers under its own bar. The nivo
							    bands are equal width, and so are these `flex-1` slots. */}
							{/* R151 (audit 2026-08-28): a 7 Days / 30 Days switch changes the
							    slot count, so the dates used to jump with a hard cut. The row
							    is keyed on its own swap (150ms `form-spam-leave`, then the new
							    row plays `form-spam-panel`); the bed and the tweened counts
							    above are NOT keyed, so they keep their state. */}
							<div
								key={dateSwap.painted}
								className={`mt-1 flex pl-[34px] ${
									dateSwap.leaving ? 'form-spam-leave' : 'form-spam-panel'
								}`}
								aria-hidden
							>
								{datePoints.map((point, index) => (
									<span key={point.day} className="flex flex-1 justify-center">
										{showsDate(index, datePoints.length) ? (
											<span className="whitespace-nowrap text-[10px] leading-[14px] text-[rgb(var(--cg-text-secondary))]">
												{point.day}
											</span>
										) : null}
									</span>
								))}
							</div>
						</div>
					</div>
				<OutcomeLegend
					defs={shown}
					category={legendCategory}
					count={axisLabel}
					totals={totals}
					prev={prev}
					{...hi}
				/>
			</div>
			)}
		</StatCard>
	)
}

/**
 * The traffic quality pair: email validation on the left, bot verification on
 * the right, each a day-by-day stack. R171: the Over time / Breakdown
 * switch is gone (Esteban dropped the Breakdown draw), so the pair has one
 * way to draw and no swap.
 *
 * R191 (Esteban, 2026-09-01): the big "Traffic quality" tray around the pair is
 * gone. Each chart is a card of its own, the way the dashboard draws its
 * panels, so the two sit on one grid at the dashboard's 12px gap. The vertical
 * `Divider` left with the tray: two cards need a gap, not a rule.
 */
export const TrafficQualityPanel = ({
	email,
	bot,
	loading = false
}: {
	loading?: boolean
	email: OutcomeColumnData
	bot: OutcomeColumnData
}) => {
	// R134: the filters live here, above both columns.
	const [emailFilter, setEmailFilter] = useState<string[]>([])
	const [botFilter, setBotFilter] = useState<string[]>([])
	return (
		// `items-stretch` is the grid default, so the two cards still share one
		// height and the free space sits under the shorter legend (R140).
		// R303: `grow` takes the Traffic panel's spare height. The rows stay
		// `auto`, and a grid stretches auto rows to fill its box, so the two
		// cards grow together and never shrink under their content.
		<div className="form-spam-panel grid grow grid-cols-1 gap-3 lg:grid-cols-2">
			{/* R232 (Gustavo, 3 Sep, pin #64): titles and subtitles are his copy,
			    verbatim, capitals included. */}
			<OutcomeColumn
				loading={loading}
				title="Email Validation"
				subtitle="Daily form submissions by Deliverability"
				axisLabel="Submissions"
				// R281: the legend heads are his copy, the same words as the subtitles.
				legendCategory="Deliverability"
				filter={emailFilter}
				onFilter={setEmailFilter}
				{...email}
			/>
			<OutcomeColumn
				loading={loading}
				title="Bot Detection"
				subtitle="Daily visitor volume by Verification Result"
				axisLabel="Analyzed visitors"
				legendCategory="Verification Result"
				filter={botFilter}
				onFilter={setBotFilter}
				{...bot}
			/>
		</div>
	)
}

/**
 * HELD, R102. Nothing draws this pair any more: the tabs stopped picking a
 * layer, so the overall verdict view left the tray. `VerdictTrend`,
 * `VerdictMix`, `VerdictLegend` and the `series` / `visible` / `totals` memos
 * in the drawer are held with it. They wait for an explicit delete order.
 *
 * The overall verdict pair: the day-by-day trend on the left, the same totals
 * as a mix on the right.
 *
 * R53 lifts this out of the drawer body. It used to be inline JSX under
 * `metricsTab === 'overall'`, and the two highlight states it now needs are
 * hooks, which cannot sit inside a branch. As a component it matches
 * `OutcomePanel` and keeps its own state local.
 */
const VerdictPanel = ({
	points,
	totals
}: {
	points: VerdictPoint[]
	totals: VerdictTotals
}) => {
	const trend = useHighlight()
	const mix = useHighlight()
	return (
		<div className="form-spam-panel flex flex-col cg-bg-surface">
			<div className="flex items-stretch">
				<div className="flex min-w-0 flex-1 flex-col">
					<ChartHead
						icon={<LineChartUp01 />}
						title="Verdict trend"
						hint="How the share of allowed, blocked and challenged submissions moved day by day."
					/>
					<div className="p-3 pt-0">
						<ChartBed>
							<VerdictTrend points={points} hi={trend.hi} />
						</ChartBed>
					</div>
					<VerdictLegend totals={totals} {...trend} />
				</div>
				<Divider orientation="vertical" />
				<div className="flex min-w-0 flex-1 flex-col">
					<ChartHead
						icon={<PieChart01 />}
						title="Verdict mix"
						hint="The share each verdict took of all submissions in this period."
					/>
					<div className="p-3 pt-0">
						<VerdictMix totals={totals} {...mix} />
					</div>
					<VerdictLegend totals={totals} {...mix} />
				</div>
			</div>
		</div>
	)
}

/**
 * The unlink ask. A destructive header action needs a stop before it fires:
 * the button opens this, and only Confirm removes the form. Same siting
 * rules as RequestSourceDialog — a SIBLING of the Sheet (a transformed
 * ancestor would trap the fixed overlay), zIndex 70 over the sheet's 50.
 */
const UnlinkConfirmDialog = ({
	open,
	busy,
	name,
	onClose,
	onConfirm
}: {
	open: boolean
	busy?: boolean
	name: string
	onClose: () => void
	onConfirm: () => void
}) => {
	const modal = useEnterExit(open, { duration: 200 })
	if (!modal.mounted) return null
	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			rounded="xl"
			zIndex={70}
			className={`${CONFIRM_MODAL_WIDTH} !animate-none transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
				modal.entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
			}`}
			overlayClassName={`!animate-none transition-opacity duration-200 motion-reduce:transition-none ${
				modal.entered ? 'opacity-100' : 'opacity-0'
			}`}
		>
			<div className="flex flex-col gap-5 p-6">
				<DialogHeader
					title="Unlink form"
					subtitle={`New submissions to “${name}” go through unchecked, and its history leaves this list. The form stays on your page.`}
					onClose={onClose}
				/>
				<div className="flex justify-end gap-3">
					<AppButton
						type="button"
						variant="secondary"
						size="md"
						label="Cancel"
						onClick={onClose}
					/>
					<AppButton
						type="button"
						variant="destructive"
						size="md"
						label="Unlink form"
						disabled={busy}
						leftIcon={busy ? <DotMatrix size="xs" /> : undefined}
						onClick={onConfirm}
					/>
				</div>
			</div>
		</Modal>
	)
}

/* --------------------------------- drawer -------------------------------- */

export const FormDetailDrawer = ({
	formId,
	onClose,
	onViewSubmissions
}: FormDetailDrawerProps) => {
	const {
		api,
		setup,
		canWrite,
		getForm,
		updateForm,
		removeForm,
		crmAccounts,
		addCrmAccount
	} = useFormSpam()

	// Hold the last real record so the close animation has content to show
	// after an unlink removes the form from the store.
	const live = formId ? getForm(formId) : undefined
	const heldRef = useRef<FormRecord | undefined>(undefined)
	if (live) heldRef.current = live
	const form = live ?? heldRef.current

	const [tab, setTab] = useState('traffic')
	// `tab` is what the Tabs control shows, so the press is answered at once.
	// `paintedTab` is what the body renders, and it LAGS by one exit: the
	// outgoing panel plays `form-spam-leave`, then the incoming one mounts and
	// plays `form-spam-panel`. Mounting both to crossfade would build the
	// traffic panel's two nivo trees while nobody is looking at them.
	const [paintedTab, setPaintedTab] = useState('traffic')
	const [leaving, setLeaving] = useState(false)
	const [range, setRange] = useState<Range>('30d')
	const [editingName, setEditingName] = useState(false)
	const [draftName, setDraftName] = useState('')
	const nameButtonRef = useRef<HTMLButtonElement>(null)
	// Callback refs, not `useRef`. The DS `Sheet` returns null while it is
	// closed, so both nodes arrive LATE, and `useInert` reads `ref.current`
	// inside an effect keyed on [ref, on]. A plain `useRef` never changes that
	// key, so the hook would run once against a null node and the hidden half of
	// the pair would stay reachable for the rest of the session. A `useMemo`
	// over the node hands the hook a NEW ref object the moment the node lands.
	// DS GAP TO REPORT: `useInert` does not re-apply when its ref fills in
	// later. Do not patch the DS from here.
	const [nameInputEl, setNameInputEl] = useState<HTMLInputElement | null>(null)
	const [nameH2El, setNameH2El] = useState<HTMLHeadingElement | null>(null)
	const nameInputRef = useMemo(() => ({ current: nameInputEl }), [nameInputEl])
	const nameH2Ref = useMemo(() => ({ current: nameH2El }), [nameH2El])
	// True when the rename ends from the KEYBOARD (Enter, Escape, or the
	// drawer's own close request). Those three leave the focus on the input,
	// so it has to go back to the title. A rename ended by a click elsewhere
	// leaves the focus where the user put it.
	const returnNameFocus = useRef(false)
	// R70: the two nodes now share one grid cell and cross-fade, so the hidden
	// one is still in the DOM. `inert` is the only thing that takes it out of
	// BOTH the tab order and the accessibility tree; `opacity: 0` alone leaves
	// a phantom heading and a phantom input for a screen reader to read.
	useInert(nameInputRef, !editingName)
	useInert(nameH2Ref, editingName)
	// `autoFocus` fires on mount only, and neither node unmounts now, so the
	// focus moves by hand. Declared AFTER both `useInert` calls: an inert node
	// cannot take focus, and effects run in declaration order.
	useEffect(() => {
		if (editingName) {
			nameInputRef.current?.focus()
			return
		}
		if (!returnNameFocus.current) return
		returnNameFocus.current = false
		nameButtonRef.current?.focus()
	}, [editingName])
	const [mutationError, setMutationError] = useState('')
	const [saving, setSaving] = useState(false)
	const savingRef = useRef(false)
	const [crmDraft, setCrmDraft] = useState<CrmConnections | null>(null)
	const [crmDraftOn, setCrmDraftOn] = useState<boolean | null>(null)
	useEffect(() => {
		setCrmDraft(null)
		setCrmDraftOn(null)
	}, [live?.id])
	const [request, setRequest] = useState<RequestState>('idle')
	const [requestName, setRequestName] = useState('')
	const [confirmUnlink, setConfirmUnlink] = useState(false)

	// R49 and R69 held a pending bot pick here (degree + target) until the
	// consent row committed both. R277 (Esteban, 4 Sep, item 6): the row is
	// gone and the switches write the record at once, so the pick is gone too.
	const requestTimer = useRef<number>()

	// A connect here is real for the record: the popup's success message
	// patches the form, so the row stays connected after a reopen.
	//
	// `live`, NOT `form`. A closed drawer still holds the last record in
	// `heldRef` so its exit has content to draw, and its message listener is
	// still mounted. Patch on `form` and a connect made in the link wizard
	// lands on whichever form the user looked at last.
	const {
		crm: crmLocal,
		connectCrm,
		reset: resetCrm,
		error: crmError
	} = useCrmConnect((provider, account) => {
			if (!live) return
			const held = addCrmAccount({ provider, ...account })
			// R283 (Esteban, 7 Sep): one CRM at a time. The new login replaces
			// whatever the other CRM held, the same as a menu pick.
		if (api) setCrmDraft({ brevo: null, hubspot: held.id })
		else void updateForm(live.id, { crm: { brevo: null, hubspot: null, [provider]: held.id } })
	})

	// Ralph, 2026-08-20: ClickGuard never creates the CRM field. The user
	// creates it, and this pair names it and reports what a look for it found.
	// `fieldProvider` is the CRM that connected last, so the panel says the
	// right product name.
	const crmSelectionRef = useRef('')
	crmSelectionRef.current = `${live?.id}|${
		crmDraft ? crmDraft.hubspot : live?.crm.hubspot
	}`
	const {
		fieldState,
		checkField,
		resetField,
		error: fieldError
	} = useCrmField(
		api && live
			? async () => {
					const connectionId =
						(crmDraft ? crmDraft.hubspot : live.crm.hubspot) || ''
					const selectedIdentity = `${live.id}|${connectionId}`
					const result = await createCrmApi(api.accountId).verify(
						connectionId,
						api.assetId,
						live.id
					)
					if (crmSelectionRef.current !== selectedIdentity)
						throw new Error(
							'The selected connection changed. Verify the current selection.'
						)
					await updateForm(live.id, {
						crm: { brevo: null, hubspot: connectionId },
						crmVerificationToken: result.verification_token,
						crmFlag: crmDraftOn ?? live.crmFlag
					})
					setCrmDraft(null)
					setCrmDraftOn(null)
					return result
			  }
			: undefined,
		`${live?.id}|${crmDraft?.hubspot || live?.crm.hubspot || ''}`,
		crmDraft ? undefined : live?.raw?.flag_crm.verification
	)

	// R262: the Setup tab carries the wizard's widget step. A check that finds
	// the snippet writes `widget: true` to the record, so the table's "Needs
	// setup" word clears with it. The hook has no "found" seed, so a form stored
	// with the widget already found shows the step idle until the next check.
	const { widgetState, checkWidget, resetWidget } = useWidgetCheck(
		api && live
			? async () => {
					const result = await api.verify(
						live.url,
						live.slug || '',
						live.selector || null
					)
					return result.verified
			  }
			: undefined,
		`${live?.id}|${live?.url}|${live?.selector}|${live?.slug}`
	)
	useEffect(() => {
		if ((widgetState === 'found' || widgetState === 'missing') && live && (!api || canWrite))
			void updateForm(live.id, { widget: widgetState === 'found', widgetKnown: true }).catch(error => setMutationError(errorMessage(error)))
		// eslint-disable-next-line
	}, [widgetState])

	// R262: the wizard's URL check, on the record. The URL the drawer opened on
	// is trusted (the form is linked), so the line reads "Form detected" at
	// once; an edit re-checks the way the wizard does. The pair is set in render
	// so the first paint of a new form already holds its URL. R277: the drawer's
	// URL field is read only, so no edit comes and the line holds "Form detected".
	const openedFor = useRef<string | null>(null)
	const openedUrl = useRef('')
	if (!formId) openedFor.current = null
	else if (formId !== openedFor.current && live) {
		openedFor.current = formId
		openedUrl.current = live.url
	}
	const checkedUrl = useUrlVerify(live?.url ?? '', !!live, openedUrl.current)
	const urlVerify = live?.verificationMode === 'browser'
		? 'idle'
		: api && live?.raw?.email_field_detected === false ? 'missing' : checkedUrl

	// R151 (audit 2026-08-28): the field check is one flag, not one per CRM.
	// The panel names Brevo first, so a HubSpot "Found" rode over to the
	// Brevo recipe when a second CRM connected. A change of the named
	// provider starts the check over. A change TO null is left alone: the
	// panel is leaving then, and an idle state would flash under the exit.
	// The same derivation stands below the empty-drawer return for the JSX.
	const namedProvider = form?.crm.brevo
		? 'brevo'
		: form?.crm.hubspot
		? 'hubspot'
		: null
	useEffect(() => {
		if (!api && namedProvider) resetField()
		// eslint-disable-next-line
	}, [namedProvider])

	// A fresh form starts on the first tab with the widest range.
	useEffect(() => {
		if (formId) {
			setEditingName(false)
			setTab('traffic')
			setPaintedTab('traffic')
			setLeaving(false)
			setRange('30d')
			setRequest('idle')
			setRequestName('')
			setConfirmUnlink(false)
			// R277: the R49/R69 bot pick pair cleared here; the pair is gone.
			if (!api) resetField()
			window.clearTimeout(requestTimer.current)
			resetCrm()
			resetWidget()
		}
		// eslint-disable-next-line
	}, [formId])

	useEffect(
		() => () => {
			window.clearTimeout(requestTimer.current)
		},
		[]
	)

	// Play the outgoing panel's exit, then swap the painted panel. The timer
	// matches the 150ms of `form-spam-leave`.
	useEffect(() => {
		if (tab === paintedTab) return
		setLeaving(true)
		const timer = window.setTimeout(() => {
			setPaintedTab(tab)
			setLeaving(false)
		}, 150)
		return () => window.clearTimeout(timer)
	}, [tab, paintedTab])

	const report = useLeadFormStats(
		formId ? api : undefined,
		range === '7d' ? 7 : 30,
		formId || undefined,
		live
	)
	const botReport = chartData(report.stats, 'bot')
	const emailReport = chartData(report.stats, 'email')
	const botVisible = api
		? botReport.points
		: (form ? getBotOutcomeSeries(form) : []).slice(range === '7d' ? -7 : -30)
	const emailVisible = api
		? emailReport.points
		: (form ? getEmailOutcomeSeries(form) : []).slice(range === '7d' ? -7 : -30)
	const botTotals = api
		? botReport.totals
		: outcomeTotals(botVisible, BOT_OUTCOMES)
	const emailTotals = api
		? emailReport.totals
		: outcomeTotals(emailVisible, EMAIL_OUTCOMES)
	const botPrev = api
		? botReport.prev
		: previousOutcomeTotals(botTotals, BOT_OUTCOMES, `${form?.id}/bot/${range}`)
	const emailPrev = api
		? emailReport.prev
		: previousOutcomeTotals(
				emailTotals,
				EMAIL_OUTCOMES,
				`${form?.id}/email/${range}`
	)

	const botBed = useMemo(
		() =>
			countRows(
				Math.max(
					1,
					...botVisible.map((p) =>
						BOT_OUTCOMES.reduce((sum, def) => sum + (p.counts[def.key] ?? 0), 0)
					)
				)
			),
		[botVisible]
	)
	const emailBed = useMemo(
		() =>
			countRows(
				Math.max(
					1,
					...emailVisible.map((p) =>
						EMAIL_OUTCOMES.reduce(
							(sum, def) => sum + (p.counts[def.key] ?? 0),
							0
						)
					)
				)
			),
		[emailVisible]
	)

	// R257: the period switch lives in the header tabs row and belongs to the
	// Traffic tab alone, so it fades with the tab change and stays mounted for
	// the exit (a bare ternary would cut it).
	const rangeSwitch = useEnterExit(tab === 'traffic', { duration: 150 })

	// The CRM connection asks for the user's word first. Ralph, product sync
	// 2026-08-20: the account must choose the action, or the liability of that
	// action is ours, not theirs. R49 took the bot degrees off this dialog: they
	// ask on the consent row inside the card.
	//
	// The hook stands ABOVE the empty-drawer return. React counts the hooks of
	// every render, and a hook below that return runs on some renders only.
	const { ask, closeAsk } = useAuthorization()

	if (!form) {
		return (
			<Sheet
				open={false}
				onClose={onClose}
				width={980}
				hideCloseButton
				className="form-spam-drawer"
				overlayClassName="form-spam-drawer__overlay"
			/>
		)
	}

	const patch = async (next: Partial<FormRecord>) => {
		if (savingRef.current || (api && !canWrite)) return false
		savingRef.current = true
		setSaving(true)
		setMutationError('')
		try {
			await updateForm(form.id, next)
			return true
		} catch (error) {
			setMutationError(errorMessage(error))
			return false
		} finally {
			savingRef.current = false
			setSaving(false)
		}
	}

	// R49 through R69 held the drawer half of the consent here: the wizard has
	// a Next button, this surface has none, so the checkbox was the commit and
	// a degree that owed a word waited in a pick. R277 (Esteban, 4 Sep, item
	// 6): the box is gone. Every switch writes straight to the record.
	const savedLevel = botLevelOf(form)

	// R277: the master switch writes its flag alone. The stored degree and
	// target stand, so a switch off and on comes back to the same pair. (R69's
	// drop to Monitor on the way back served the consent box, gone with it.)
	const pickMaster = (next: boolean) => patch({ captcha: next })

	// R262: the Setup tab shows the wizard's two switches over the ONE stored
	// level and trigger. Block on = `block`. CAPTCHA on = `challenge`, or
	// `block` aimed at every suspect. Both on stores `block` + `suspected`, the
	// pair the wizard's header names as its data-model gap; the drawer keeps it.
	const blockOn = form.blockEnabled ?? savedLevel === 'block'
	const captchaOn =
		form.captchaEnabled ??
		(savedLevel === 'challenge' ||
			(blockOn && form.captchaTrigger === 'suspected'))

	// R277: a switch writes its (level, trigger) pair at once. R49's pending
	// pick and its "same as the record" early return went with the box.
	const pickSwitches = (level: BotLevel, trigger: CaptchaTrigger) =>
		patch(botLevelPatch(level, trigger))
	const pickCaptcha = (next: boolean) => patch({ captchaEnabled: next })
	const pickBlock = (next: boolean) => patch({ blockEnabled: next })
	const commitName = async () => {
		const next = draftName.trim()
		if (!next || next === form.name || (await patch({ name: next })))
		setEditingName(false)
	}

	// Escape (and any other close request) first closes an open ask
	// (request, then unlink, then the authorization ask), then cancels an
	// open rename; only the next one closes the drawer.
	const handleClose = () => {
		if (request === 'open') {
			setRequest('idle')
			return
		}
		if (confirmUnlink) {
			setConfirmUnlink(false)
			return
		}
		// R151 (audit 2026-08-28): the DS Modal and the DS Sheet each close on
		// their own `document` Escape listener in the SAME dispatch, so a Pause
		// or Connect ask took the drawer down with it. The state is still set
		// here whichever listener ran first, so the ask closes and the drawer
		// stays.
		if (ask) {
			closeAsk()
			return
		}
		if (editingName) {
			returnNameFocus.current = true
			setEditingName(false)
			return
		}
		onClose()
	}
	const sendRequest = () => {
		if (!requestName.trim()) return
		setRequest('sending')
		if (api)
			void createCrmApi(api.accountId)
				.request(requestName)
				.then(() => setRequest('sent'))
				.catch((e) => {
					setRequest('open')
					setMutationError(errorMessage(e))
				})
		else requestTimer.current = window.setTimeout(() => setRequest('sent'), 1400)
	}

	// Derived, not stored: the field panel names whichever CRM holds an
	// account, so a disconnect takes the panel with it and nothing can go out
	// of step. The record wins over the popup's transient state.
	const selectedCrm = crmDraft || form.crm
	const fieldProvider = selectedCrm.brevo
		? 'brevo'
		: selectedCrm.hubspot
		? 'hubspot'
		: null
	// R262: the CRM switch. `crmFlag` is what the wizard wrote; a form without
	// it (older seeds) follows the connection, so a connected CRM reads as on.
	const crmOn = crmDraftOn ?? form.crmFlag ?? fieldProvider !== null
	const connectedAccount = fieldProvider
		? crmAccounts.find((account) => account.id === selectedCrm[fieldProvider])
		: undefined
	// "Connect another CRM": R151 holds one CRM per form, so the new login
	// replaces the old one. The picker folds back in and the field check
	// starts over, the same as the wizard.
	const connectAnother = () => {
		if (api) setCrmDraft({ brevo: null, hubspot: null })
		else void updateForm(form.id, { crm: { brevo: null, hubspot: null } })
		resetCrm()
		resetField()
	}

	// R279 (Esteban, 4 Sep, items 8, 9): the pill's menu picks among the held
	// logins. A pick writes that login as the record's one connection and
	// starts the field check over (R152; the `namedProvider` effect only fires
	// when the CRM changes, so a same-CRM pick needs the explicit reset).
	// R312: the menu's Reconnect only closes the menu now, the same as Manage;
	// the beat that wrote the login back to connected is gone (see
	// `ConnectedCrmRow`).
	const lastSubmissionAt = report.stats?.forms.find(
		(row) => row.form_id === form.id
	)?.last_submission_at
	const lastSubmissionLabel = api
		? formatRelativeShort(lastSubmissionAt) ?? 'No traffic yet'
		: form.lastSeen ?? 'No traffic yet'
	const linkedDate = form.raw?.created_at ? new Date(form.raw.created_at) : null
	const linkedLabel = api
		? linkedDate && !Number.isNaN(linkedDate.getTime())
			? linkedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
			: '—'
		: getLinkedLabel(form)

	const pickAccount = (account: CrmAccount) => {
		if (api) setCrmDraft({ brevo: null, hubspot: account.id })
		else
			void updateForm(form.id, {
				crm: { brevo: null, hubspot: null, [account.provider]: account.id }
			})
		resetField()
	}


	return (
		<>
		<Sheet
			open={!!formId && !!live}
			onClose={handleClose}
			width={980}
			hideCloseButton
			className="form-spam-drawer"
			overlayClassName="form-spam-drawer__overlay"
		>
			{/* The Figma sheet floats: the DS panel goes transparent (see
			   `.form-spam-drawer`) and this div is the visible card. The card clips
			   the 10px corners and does not scroll; the header and the tabs stay
			   fixed, and the body wrapper below the tabs owns the scroll, as in the
			   Figma split (frame 45:35673 fixed, frame 45:35702 scrolls). */}
			<div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] cg-bg-surface-subtle">
				{/* -------- fixed header: identity, actions, headline metrics --------
				   This container owns the single bottom hairline. The tab row draws
				   none of its own (no fullBleed filler, inactive border made
				   transparent in the css); only the active tab's 2px brand border
				   stays, as the indicator.

				   2026-08-25, his ask: the hairline is `--cg-border` (#e1e1e1), the
				   default border token, not `--cg-subtle` (#ededed). The modal sheet
				   already took the same token at R75, for the same reason: #ededed
				   against white reads as nothing. */}
				<div className="relative shrink-0 border-b border-[rgb(var(--cg-border))] cg-bg-surface">
					{/* 08-18 request: an X in the card's top corner. It goes through the
					   same wrapped close as Escape, so an open rename only cancels first.
					   08-20 follow-up: "this smaller modal's X icon is bigger than the
					   modal behind, just make them the same". So the drawer drops its
					   hand-rolled close and takes the shared `CloseButton`, the same 20px
					   X in a 32px target that every dialog in this lane draws. The offset
					   is 8px, not 12px: the target grew 24 -> 32, so 8px keeps the glyph
					   itself in the exact place it already sat. */}
						<CloseButton
							onClick={handleClose}
							className="absolute right-2 top-2"
						/>
					{/* One row, per Figma 45:35675: the identity column with its
					   buttons below the text, a full-height divider, then the three
					   metric tiles. The labeled buttons carry no icons in the design;
					   the title itself (08-19, replaces the 08-18 pencil) starts the
					   inline rename. */}
					<div className="flex items-stretch gap-6 p-4">
						<div className="flex w-[301px] min-w-0 shrink-0 flex-col gap-4">
							<div className="flex min-w-0 flex-col gap-1.5">
								{/* R70: the title and its rename box used to swap with a bare
								   ternary, so whichever one left did it in a single frame. Both
								   stay mounted in ONE grid cell now and cross-fade in place over
								   150ms. Both carry the same 26px line, so the cell never
								   changes height and nothing under it moves. `inert` (above)
								   holds the hidden one out of the tab order and out of the
								   accessibility tree. */}
								<div className="grid min-w-0">
									<input
										ref={setNameInputEl}
										aria-label="Form name"
										value={draftName}
										onChange={(e) => setDraftName(e.target.value)}
										onFocus={(e) => e.target.select()}
										onBlur={() => {
											// `inert` blurs the input when the rename ends, so this
											// fires a second time on the way out. Escape has already
											// cleared the flag by then, and a commit there would put
											// back the very draft the user cancelled.
											if (editingName) commitName()
										}}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												returnNameFocus.current = true
												commitName()
											}
											if (e.key === 'Escape') {
												// The Sheet also listens for Escape: this press only
												// cancels the rename and must not close the drawer.
												e.stopPropagation()
												returnNameFocus.current = true
												setEditingName(false)
											}
										}}
										className={`form-spam-name-swap -mx-1 w-full rounded-[4px] cg-bg-surface-2 px-1 text-[20px] font-semibold leading-[26px] cg-text-text-primary outline-none transition-opacity duration-150 motion-reduce:transition-none ${
											editingName ? 'opacity-100' : 'opacity-0'
										}`}
									/>
									<h2
										ref={setNameH2El}
										className={`form-spam-name-swap min-w-0 [&>div]:flex [&>div]:w-full transition-opacity duration-150 motion-reduce:transition-none ${
											editingName ? 'opacity-0' : 'opacity-100'
										}`}
									>
										{/* 08-19: the title itself is the rename trigger; the
										   pencil button below it is gone. cursor-text + the hover
										   plate (the input's resting bg) say "click to edit". */}
										<Tooltip content="Rename form" portal>
											<button
												ref={nameButtonRef}
												type="button"
												aria-label="Rename form"
												disabled={saving || (!!api && !canWrite)}
												onClick={() => {
													setDraftName(form.name)
													setEditingName(true)
												}}
												className="-mx-1 block w-full cursor-text truncate rounded-[4px] px-1 text-left text-[20px] font-semibold leading-[26px] cg-text-text-primary transition-colors duration-150 hover:bg-[rgb(var(--cg-surface-2))] focus-visible:bg-[rgb(var(--cg-surface-2))] focus-visible:outline-none motion-reduce:transition-none"
											>
												{form.name}
											</button>
										</Tooltip>
									</h2>
								</div>
								<div className="flex min-w-0 items-center gap-1 text-[14px] leading-[18px]">
										<span className="shrink-0 cg-text-text-secondary">
											Settings for
										</span>
										<div className="min-w-0 [&>div]:max-w-full">
											<Tooltip content={form.url} portal>
												<span className="cursor-default truncate cg-text-text-secondary">
													{form.url}
												</span>
											</Tooltip>
										</div>
								</div>

								<div className="flex items-center gap-1.5 whitespace-nowrap text-[14px] leading-[18px]">
									<Tooltip content={linkedDate?.toLocaleString() ?? linkedLabel} portal>
										<span className="flex items-center gap-1 cg-text-text-secondary">
											<span>Linked</span>
											<span>{linkedLabel}</span>
										</span>
									</Tooltip>
									<span aria-hidden className="text-[rgb(var(--cg-border))]">&bull;</span>
									<Tooltip content={lastSubmissionAt ? new Date(lastSubmissionAt).toLocaleString() : lastSubmissionLabel} portal>
										<span className="flex items-center gap-1 cg-text-text-secondary">
											<span>Last submission</span>
											<span>
												{api && report.loading ? <DotMatrix size="xs" label="Loading last submission" /> : lastSubmissionLabel}
											</span>
										</span>
									</Tooltip>
								</div>
							</div>
							<div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
								<AppButton
									variant="secondary"
									size="md"
									label="Unlink form"
										disabled={saving || (!!api && !canWrite)}
									onClick={() => setConfirmUnlink(true)}
								/>
								{/* R290 (Esteban, 10 Sep): "lose the pause protection thing". The
								   Pause / Resume protection button (R114, R137, R141) is gone. The
								   `paused` status stays in the data, the facet pill and the status
								   cell, because the seeds carry paused forms; nothing on this
								   surface flips it now. */}
								{/* R298 (Gustavo, 11 Sep, pin #100): the feed left the drawer for the
								   page's Submissions tab, so the header points there. R302 (Esteban,
								   11 Sep): "make that a normal primary purple one to the right of
								   unlink, without the arrow icon". The DS primary closes the row as
								   the main action; Unlink stays secondary before it. The page always
								   passes the handler; the guard is for a bare mount. */}
								{onViewSubmissions ? (
									<AppButton
										variant="primary"
										size="md"
										label="View submissions"
										onClick={() => onViewSubmissions(form.id)}
									/>
								) : null}
							</div>
						</div>
						<Divider orientation="vertical" />
						<MetricTile
							className="flex-1"
							icon={<Send01 />}
							label={
								<TileLabel
									text="Form submissions"
									info="Every submission this form received while protection was on."
								/>
							}
								value={api ? report.loading ? <DotMatrix size="sm" /> : report.stats ? formatCount(report.stats.metrics.submissions) : '—' : formatCount(form.submissions)}
						/>
						<Divider orientation="vertical" />
						<MetricTile
							className="flex-1"
							icon={<Flag01 />}
							label={
								<TileLabel
									text="Flagged submissions"
										info="Submissions matching the form’s email protection flags when they were processed."
								/>
							}
								value={api ? report.loading ? <DotMatrix size="sm" /> : report.stats ? formatCount(report.stats.metrics.flagged) : '—' : formatCount(form.invalidEmails)}
						/>
						<Divider orientation="vertical" />
						{/* R317 (Gustavo pin #83, his #85): a See documentation link. R317 put
						   it under the header buttons, at the end of the actions. R318
						   (Esteban, 11 Sep, /ss): "place it here instead, it's bad where it
						   is", his box drawn in the header's bottom-right corner, under the
						   Blocked bots tile. The last tile column becomes a flex column: the
						   tile keeps the top, the link sits at the bottom right edge, on the
						   row's bottom line with the buttons across the header. The URL is
						   the tracking-code KB article the setup screen already links; the
						   Form Spam article does not exist yet. */}
						<div className="flex min-w-0 flex-1 flex-col">
							<MetricTile
								className="flex-1"
								icon={<ShieldOff />}
								label={
									<TileLabel
										text="Blocked bots"
										info="Submissions the protection rejected before they reached you."
									/>
								}
									value={api ? report.loading ? <DotMatrix size="sm" /> : report.stats ? formatCount(report.stats.metrics.blocked) : '—' : formatCount(form.blocked)}
							/>
							{(!api || setup?.help_url) && <a
								href={api ? setup?.help_url : FORM_SPAM_KB_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex shrink-0 items-center gap-1.5 self-end text-[14px] font-medium leading-[20px] cg-text-brand underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
							>
								See documentation
								<LinkExternal01 className="h-4 w-4 shrink-0" aria-hidden />
							</a>}
						</div>
					</div>
					{/* R257 (Esteban, 3 Sep): "put the 30/7 days tab in the white
					   container, at the height of the traffic and setup tabs, all the
					   way to the right". The period switch left the body row (dead
					   space beside it) and sits here, the PageHeader `tabsActions`
					   pattern. Both boxes measure 36px, so the row keeps the tab
					   underline on the header hairline and centres the switch on the
					   tab label.
					   R304 (Esteban, 11 Sep): "this needs a bit more space between the
					   tabs and the border". Centred on the 36px tab box, the 30px
					   switch sat 3px above the header hairline. The row now pins the
					   tabs to the hairline (`items-end`) and the switch carries
					   `mb-1.5`, so it sits 6px above the hairline and 6px below the
					   row's top, 3px above the tab label's centre. The header keeps
					   its height. */}
					<div className="flex items-end justify-between gap-3 px-4 pt-1.5">
						<Tabs size="sm" value={tab} onChange={setTab}>
							<TabList>
								<Tab value="traffic" label="Traffic" />
								<Tab value="setup" label="Setup" />
							</TabList>
						</Tabs>
						{rangeSwitch.mounted ? (
							<div
								className={`mb-1.5 transition-opacity duration-150 motion-reduce:transition-none ${
									rangeSwitch.entered ? 'opacity-100' : 'opacity-0'
								}`}
							>
								{/* R266 (Esteban, 4 Sep): the DS small size; the regular one
								    outweighed the tabs beside it. */}
								<SegmentedTabs
									size="small"
									value={range}
									onChange={(next) => setRange(next as Range)}
								>
									<SegmentedTabList>
										<SegmentedTab value="7d" label="7 Days" />
										<SegmentedTab value="30d" label="30 Days" />
									</SegmentedTabList>
								</SegmentedTabs>
							</div>
						) : null}
					</div>
				</div>

				{/* ------------ body: the only scroll region of the card ------------
				   R298 (Gustavo, 11 Sep, pin #100): the virtuoso scroll-parent marker
				   left with the feed's virtualised table. Nothing in the drawer
				   virtualises now. */}
					{mutationError && (
						<p role="alert" className="px-4 py-2 cg-text-danger">
							{mutationError}
						</p>
					)}
					{saving && (
						<div
							role="status"
							aria-label="Saving form"
							className="flex justify-center p-2"
						>
							<DotMatrix size="xs" />
						</div>
					)}
				<div className="min-h-0 flex-1 overflow-y-auto">
					{paintedTab === 'traffic' ? (
						<div
							key="traffic"
							// R303 (Esteban, 11 Sep): the panel fills the body, so the
							// chart pair below can grow into the space under the cards.
							className={`flex min-h-full flex-col gap-3 p-4 ${
								leaving ? 'form-spam-leave' : 'form-spam-panel'
							}`}
						>
							{/* Round 40: the section title went back inside the card. R171: the
							   mode switch is gone. R257: the period switch moved up to the header
							   tabs row, so this panel opens on the charts. */}
							{/* Verdict metrics — Figma 45:35708. R171: the stack is the
							   only draw, so no mode key remains.
							   R191: the gray "Traffic quality" tray around the pair is gone.
							   `TrafficQualityPanel` now draws two cards of its own, the same
							   way the dashboard does, so there is nothing left to wrap. */}
								{api && report.error ? (
									<p role="alert">{report.error}</p>
								) : (
							<TrafficQualityPanel
								loading={!!api && report.loading}
								email={{
									points: emailVisible,
									defs: EMAIL_OUTCOMES,
									bed: emailBed,
									totals: emailTotals,
									prev: emailPrev
								}}
								bot={{
									points: botVisible,
									defs: BOT_OUTCOMES,
									bed: botBed,
									totals: botTotals,
									prev: botPrev
								}}
							/>
								)}

							{/* R298 (Gustavo, 11 Sep, pin #100): the Submissions feed left this
							   panel for the page's Submissions tab, one table over every form.
							   The header's View submissions action opens it on this form. R268's
							   retention line had already left; the period still lives on the
							   record. */}
						</div>
					) : (
						/* R262 (Esteban, 3 Sep): "mirror whats going on in the modal
						   here in the setup drawer / ofc not the same, no left pane
						   and such, but the content yeah". The four wizard panes
						   (`form-setup-panes.tsx`) stacked on the gray backdrop, no
						   rail: Form details, Protect my ads, Flag invalid submissions
						   on my CRM, Block bot submissions. Each pane reads the record
						   and writes it back through `patch`, so the modal and this
						   tab are the same fields by construction (R106's order holds).
						   A DS `Divider` sits between panes with 24px on each side.

						   Gone with it: the per-form account rows (`CrmStep`), the
						   three-level `BotStep` with its trigger segments and reach
						   estimate, and the old "Form details" card copy. */
							<fieldset
							key="setup"
								disabled={saving || (!!api && !canWrite)}
								aria-disabled={saving || (!!api && !canWrite)}
								style={
									saving || (!!api && !canWrite)
										? { pointerEvents: 'none' }
										: undefined
								}
							className={`flex flex-col gap-6 p-4 ${
								leaving ? 'form-spam-leave' : 'form-spam-panel'
							}`}
						>
							{/* R277 (Esteban, 4 Sep, item 7): the drawer's shape. The URL is
							   read only (a linked form keeps its URL) and the name field is
							   gone; the header rename above owns the name. */}
							<DetailsPane
								mode="edit"
								url={form.url}
								name={form.name}
								verify={urlVerify}
								onUrl={(next) => patch({ url: next })}
							/>
							<Divider />
							<EmailPane
								on={form.emailValidation}
								rules={form.emailRules}
								threshold={form.blockThreshold}
								onThreshold={(next) => patch({ blockThreshold: next })}
								warn={
									needsSetup(form) && !form.emailValidation && !form.captcha
										? SETUP_WARN.off
										: null
								}
								onChange={({ on, rules }) => {
									// R292: the switch and the rules land in ONE patch, so
									// the last rule off never leaves the master on alone.
									// The legacy 4-key map stays derived so older
									// readers keep a truthful view.
									patch({
										emailValidation: on,
										emailRules: rules,
										emailChecks: {
											syntax: rules.invalid,
											domain: rules.invalid,
											disposable: rules.doNotMail,
											roleBased: rules.doNotMail
										}
									})
								}}
							/>
							<Divider />
							<fieldset disabled={api && !canWrite} className="min-w-0">
								{(crmError || fieldError) && (
									<p role="alert" className="text-sm text-red-600">
										{crmError || fieldError}
									</p>
								)}
								<CrmPane
									on={crmOn}
									onToggle={(next) => {
										if (
											api &&
											next &&
											(crmDraft || !form.crm.hubspot || !form.raw?.flag_crm.verification)
										) {
											setCrmDraftOn(true)
										} else {
											setCrmDraftOn(null)
											void patch({ crmFlag: next })
										}
									}}
									crm={crmLocal}
									onConnect={connectCrm}
									request={request}
									onRequest={() => setRequest((prev) => (prev === 'idle' ? 'open' : prev))}
									connected={
										fieldProvider && connectedAccount
											? { provider: fieldProvider, account: connectedAccount }
											: null
									}
									accounts={crmAccounts}
									onPick={pickAccount}
									onConnectAnother={connectAnother}
									fieldState={fieldState}
									onCheckField={checkField}
								/>
							</fieldset>
							<Divider />
							{/* cloudwork's R63 turned the two booleans into one three-step
							   level. R69 gave the level its own stored field, `botAction`.
							   R262 reads the level with botLevelOf into the two switches
							   and writes all three fields back with botLevelPatch.
							   R272: the red setup lines read the one `needsSetup` rule the
							   header badge reads; the widget line wins over the off line. */}
							<BotPane
								on={form.captcha}
								warn={
										!needsSetup(form)
											? null
											: !form.widget
											? SETUP_WARN.widget
											: SETUP_WARN.off
								}
								widgetMissing={form.widgetKnown !== false && needsSetup(form) && !form.widget}
								onToggle={pickMaster}
								captchaOn={captchaOn}
								blockOn={blockOn}
								onCaptcha={pickCaptcha}
								onBlock={pickBlock}
								/* R278: Normal / Strict writes its own field, never the trigger. */
								sensitivity={form.botSensitivity}
								onSensitivity={(next) => patch({ botSensitivity: next })}
								/* R309 (Gustavo pin #81): the CAPTCHA switch's own threshold. */
								captchaSensitivity={form.captchaSensitivity}
									onCaptchaSensitivity={(next) =>
										patch({ captchaSensitivity: next })
									}
								/* R280: the snippet's `data-action` is the stored selector's last id or
								   class token (`widgetAction`), so `form.contact` reads `contact`. */
									action={form.slug || widgetAction(form.selector)}
								widgetState={widgetState}
								onCheckWidget={checkWidget}
							/>
								{form.verificationMode === 'browser' && (
									<p className="text-sm cg-text-text-secondary">
										Open your normal signup flow and wait for the ClickGuard widget to load.
										Then click Verify within 10 minutes to confirm it loaded in your browser.
									</p>
								)}
							</fieldset>
					)}
				</div>
			</div>
		</Sheet>
		{/* A sibling of the Sheet: its panel is transformed, and a transformed
		   ancestor traps the dialog's fixed overlay. zIndex 70 beats the
		   sheet's 50. */}
		<AuthorizeDialog ask={ask} onClose={closeAsk} />
		<RequestSourceDialog
			open={request === 'open'}
			value={requestName}
			onChange={setRequestName}
			onClose={() => setRequest('idle')}
			onSend={sendRequest}
		/>
		<UnlinkConfirmDialog
				busy={saving}
			open={confirmUnlink}
			name={form.name}
			onClose={() => setConfirmUnlink(false)}
				onConfirm={async () => {
					if (savingRef.current || (api && !canWrite)) return
					savingRef.current = true
					setSaving(true)
					setMutationError('')
					try {
						await removeForm(form.id)
				setConfirmUnlink(false)
				onClose()
					} catch (error) {
						setMutationError(errorMessage(error))
						setConfirmUnlink(false)
					} finally {
						savingRef.current = false
						setSaving(false)
					}
			}}
		/>
		</>
	)
}
